import { figOutline } from './figureOutline';
import { buildBestiary } from './bestiary';
import { portraitWindow } from './rig/b3/flag';
import { classicBeasts } from './classicBeasts';
import { buildCritters } from './critters';
import { lazyRecord } from './drawBudget';
import { snowify } from './scenery';
import {
  box as rawBox,
  canvas,
  ctx2d,
  ell as rawEll,
  line as rawLine,
  litShade,
  artH,
  artW,
  artX,
  artY,
  finishArt,
  selOutline,
  toFigureGrid,
} from './pixel';
import type { Detail } from './detail';
import { densityC } from './density';
import { type FarArm, FRAMES, WALK_FRAMES } from './motions/kit';
import { LIMB, type Pose, rigFor, stanceOf } from './motions';
import { isLifeSeated } from './motions/life';
import { hero, LOOKS, OUTFITS, type FaceStyle, type Look } from './looks';
import { swordLook } from './rig/swordParts/look';

const box = rawBox;
const ell = rawEll;
const line = rawLine;

// Looks live in render/looks/; poses, motions and arms in render/motions/ (one file per group, gathered in index.ts).
export {
  LIMB,
  MOTIONS,
  type MotionId,
  motionKeys,
  motionPhase,
  type Pose,
  rigFor,
  STANCE_COUNT,
  STANCE_MOTION_IDS,
  STANCE_PH,
  stanceOf,
  stowPhase,
} from './motions';
export {
  type FaceStyle,
  type HairStyle,
  type Headwear,
  type Look,
  LOOKS,
  OUTFITS,
  type Weapon,
} from './looks';
export {
  type BodyArm,
  type Ease,
  eased,
  type FarArm,
  FRAMES,
  type NearArm,
  type Rig,
  SHEATHE_FRAMES,
  WALK_FRAMES,
} from './motions/kit';

/** Poses sat cross-legged on the ground. */
export const isSeated = (pose: Pose): boolean =>
  pose === 'sit' || pose === 'mend' || pose === 'drink' || pose === 'lounge' || isLifeSeated(pose);

/** Action frames humanoids have on top of the basic set; beasts fall back to their basic frames. */
export const EXTRA_POSES = [
  'windup',
  'cut',
  'follow',
  'lowGuard',
  'thrust',
  'overhead',
  'chest',
  'blockHit',
  'hurt',
  'rise',
  'fall',
  'land',
  'dash',
] as const satisfies readonly Pose[];
export type ExtraPose = (typeof EXTRA_POSES)[number];

export interface SpriteSet {
  /** Prepare this view's movement cycles off the render path; notify only for finished frames. */
  prepare?: (ready: (image: HTMLCanvasElement) => void) => void;
  idle: HTMLCanvasElement[];
  walk: HTMLCanvasElement[];
  atk: HTMLCanvasElement;
  block: HTMLCanvasElement;
  cast: HTMLCanvasElement;
  sit: HTMLCanvasElement;
  jump: HTMLCanvasElement;
  /** Qinggong sprint cycle and combat-ready stance (humanoids only). */
  sprint?: HTMLCanvasElement[];
  ready?: HTMLCanvasElement[];
  /** Holds a blade (as opposed to fighting bare-handed or being a beast). */
  armed?: boolean;
  /**
   * The same frame redrawn with fabric still in motion (humanoids only). Frames are built on first
   * use and cached; `sway` and `flutter` are small integers.
   */
  cloth?: (img: HTMLCanvasElement, sway: number, flutter: number) => HTMLCanvasElement;
  /** The other camera angles of this figure (humanoids only); this set itself is `front3`. */
  views?: Partial<Record<View8, SpriteSet>>;
  /** Extra action frames (humanoids only). */
  poses: Partial<Record<ExtraPose, HTMLCanvasElement>>;
  /** Anchor (feet) inside the frame. */
  ax: number;
  ay: number;
  /** Visual height above the feet, used to place name tags. */
  h: number;
  /** Draw scale overriding the entity's gameplay scale (e.g. a boss drawn natively tall). */
  drawScale?: number;
  /**
   * Any pose drawn the first time it is asked for (townsfolk work, gestures, glances), optionally
   * with the eyes shut for a blink. Rig figures only.
   */
  extra?: (pose: Pose, ph?: number, blink?: boolean, stand?: () => HTMLCanvasElement) => HTMLCanvasElement;
  /** Wing-beat frames for creatures that fly (birds). */
  fly?: HTMLCanvasElement[];
  /**
   * Marks the figure as the player's own (rig figures): its frames are drawn ahead of everyone
   * else's, and its everyday ones in every view before they are needed (`soon`: ahead of the rest
   * of its frames; else after them, for a figure the player only sometimes is).
   */
  focus?: (soon?: boolean) => void;
  /**
   * The same figure standing in its `k`th stance (ท่ายืน, rig figures that have stances): only its
   * standing frames differ — standing, blinking, glancing, gazing, landing or stopping into a stand
   * — everything else is this set's. See `STANCE_PH`.
   */
  stance?: (k: number) => SpriteSet;
}

/** Camera angles a figure is drawn from; mirroring covers the other three directions. */
export const VIEWS = ['front', 'front3', 'side', 'back3', 'back'] as const;
export type View = (typeof VIEWS)[number];
/**
 * True eight directions (lane C0-8DIR, user 2026-10-10): the three headings that face left (SW, W, NW) are drawn
 * as their own views instead of the right-facing ones mirrored, so the weapon stays in the right hand, the robe
 * stays 右衽 and the light stays at the upper right (rig/dir8.ts). `false` brings back the mirrored five views.
 */
export const DIR8 = true;
/** The left-facing views (rig figures): drawn from `front3` / `side` / `back3` with the body's sides kept true. */
export const LEFT_VIEWS = ['front3L', 'sideL', 'back3L'] as const;
export type LeftView = (typeof LEFT_VIEWS)[number];
/** A view a rig figure can be drawn from: the five, plus the three left-facing ones. */
export type View8 = View | LeftView;
export const isLeftView = (v: View8): v is LeftView => v.endsWith('L');
/** The view a left-facing view is built from (the view itself for the five). */
export const baseView = (v: View8): View => (isLeftView(v) ? (v.slice(0, -1) as View) : v);
/** View and mirroring for each screen heading (0 = right, clockwise in 45° steps). */
const HEADING_VIEW: readonly [View, number][] = [
  ['side', 1],
  ['front3', 1],
  ['front', 0],
  ['front3', -1],
  ['side', -1],
  ['back3', -1],
  ['back', 0],
  ['back3', 1],
];

/**
 * The frames to draw for an entity heading in screen direction `heading`, and which way to mirror
 * them (straight-on views keep the entity's left/right `facing`).
 */
export function viewOf(set: SpriteSet, heading: number, facing: number): { set: SpriteSet; facing: number } {
  const [view, f] = HEADING_VIEW[((heading % 8) + 8) % 8]!;
  // A left-facing view, where the figure has one: its frames are stored mirrored back (rig/dir8.ts), so they are
  // still drawn flipped, and everything that follows the facing (trails, blades, back-worn gear) is unchanged.
  const left = DIR8 && f < 0 ? set.views?.[`${view}L` as LeftView] : undefined;
  if (left) return { set: left, facing: -1 };
  // A figure's own straight-on view (front, back) is drawn as it is, never mirrored by the side it last faced: mirrored,
  // the weapon in the right hand swapped to the left (lane C0-POSE-FIX; test/weaponHand.test.ts).
  const own = view !== 'front3' ? set.views?.[view] : undefined;
  return { set: own || set, facing: f || (DIR8 && own ? 1 : facing) };
}

/** Scale a sprite is actually drawn at. */
export const visualScale = (e: { scale: number }, set: SpriteSet | undefined): number =>
  // Figures are drawn at their own size, and bosses (scale above 1) larger still.
  set?.drawScale !== undefined
    ? set.drawScale * (e.scale > 1 ? 1 + (e.scale - 1) * 0.5 : 1)
    : e.scale * BEAST_SCALE;

/** Beasts are drawn larger so they stay in proportion with the tall human figures. */
const BEAST_SCALE = 1.35;

/**
 * Five face designs for 3/4 view. The far eye sits at cx..cx+2, the near eye at cx+5..cx+9,
 * brows at hy-2..hy, mouth around hy+7.
 */
function animeFace(g: CanvasRenderingContext2D, p: Look, cx: number, hy: number, style: FaceStyle): void {
  const iris = p.iris!;
  const irisH = p.irisH ?? iris;
  const W = '#ffffff';
  const ey = hy + 1;
  const px = (x: number, y: number, w: number, h: number, c: string) => box(g, cx + x, y, w, h, c);
  switch (style) {
    case 'anime': {
      // Big glossy eyes, brows angled in, one-sided smile.
      px(0, hy - 2, 2, 1, p.brow);
      px(2, hy - 1, 1, 1, p.brow);
      px(4, hy - 1, 2, 1, p.brow);
      px(6, hy - 2, 2, 1, p.brow);
      px(0, ey, 3, 1, p.eye);
      px(1, ey + 1, 2, 4, iris);
      px(2, ey + 1, 1, 3, p.eye);
      px(1, ey + 4, 2, 1, irisH);
      px(1, ey + 1, 1, 1, W);
      px(5, ey, 4, 1, p.eye);
      px(9, ey + 1, 1, 1, p.eye);
      px(5, ey + 1, 4, 4, iris);
      px(6, ey + 1, 2, 3, p.eye);
      px(5, ey + 4, 4, 1, irisH);
      px(5, ey + 1, 1, 2, W);
      px(8, ey + 3, 1, 1, W);
      px(5, hy + 8, 2, 1, p.mouth);
      px(7, hy + 7, 1, 1, p.mouth);
      if (p.blush) {
        px(0, hy + 7, 2, 1, p.blush);
        px(8, hy + 7, 2, 1, p.blush);
      }
      break;
    }
    case 'sharp': {
      // Narrow slanted eyes, heavy brows sweeping up, confident smirk.
      px(-1, hy - 2, 2, 1, p.brow);
      px(1, hy - 1, 2, 1, p.brow);
      px(4, hy, 2, 1, p.brow);
      px(6, hy - 1, 2, 1, p.brow);
      px(8, hy - 2, 2, 1, p.brow);
      px(0, ey + 1, 3, 1, p.eye);
      px(1, ey + 2, 2, 1, iris);
      px(2, ey + 2, 1, 1, p.eye);
      px(5, ey + 1, 4, 1, p.eye);
      px(9, ey, 1, 1, p.eye);
      px(5, ey + 2, 4, 1, iris);
      px(6, ey + 2, 2, 1, p.eye);
      px(8, ey + 2, 1, 1, irisH);
      px(5, ey + 3, 1, 1, p.skinS);
      px(10, hy + 5, 1, 1, p.skinS);
      px(5, hy + 7, 2, 1, p.mouth);
      px(7, hy + 6, 1, 1, p.mouth);
      break;
    }
    case 'cute': {
      // Round dark eyes with big shines, rosy cheeks, open smile.
      px(1, hy - 1, 2, 1, p.brow);
      px(5, hy - 1, 3, 1, p.brow);
      px(1, ey, 2, 4, p.eye);
      px(1, ey, 1, 1, W);
      px(2, ey + 3, 1, 1, iris);
      px(5, ey, 3, 4, p.eye);
      px(4, ey + 1, 1, 2, p.eye);
      px(8, ey + 1, 1, 2, p.eye);
      px(5, ey, 2, 2, W);
      px(7, ey + 3, 1, 1, iris);
      px(-1, hy + 6, 3, 1, '#f4a0a0');
      px(8, hy + 6, 3, 1, '#f4a0a0');
      px(5, hy + 7, 3, 1, p.mouth);
      px(6, hy + 8, 1, 1, '#8a3a3a');
      break;
    }
    case 'serene': {
      // Calm half-lidded eyes of a cultivator, thin level brows, quiet mouth.
      px(0, hy - 1, 3, 1, p.brow);
      px(5, hy - 1, 4, 1, p.brow);
      px(0, ey + 2, 3, 1, p.eye);
      px(1, ey + 3, 2, 1, iris);
      px(5, ey + 2, 4, 1, p.eye);
      px(9, ey + 1, 1, 1, p.eye);
      px(5, ey + 3, 4, 1, iris);
      px(6, ey + 3, 2, 1, irisH);
      px(10, hy + 5, 1, 1, p.skinS);
      px(5, hy + 7, 2, 1, p.mouth);
      break;
    }
    case 'refSerious':
    case 'refSmirk':
    case 'refGrit':
    case 'refShout':
    case 'refCold':
    case 'refSoft': {
      // Reference look: heavy black brows in a V, white eyes with small dark irises looking in, nose shadow.
      const lash = '#1a1010';
      const irisC = p.iris ?? '#3a2018';
      const white = p.evil ? '#e6dce8' : '#f6f2ec';
      const lip = '#7a3a32';
      const angry = style === 'refGrit' || style === 'refShout';
      if (style === 'refSoft') {
        // Thin arched brows.
        px(-1, hy - 2, 1, 1, p.brow);
        px(0, hy - 3, 2, 1, p.brow);
        px(2, hy - 2, 1, 1, p.brow);
        px(5, hy - 2, 1, 1, p.brow);
        px(6, hy - 3, 2, 1, p.brow);
        px(8, hy - 2, 1, 1, p.brow);
      } else {
        px(-1, hy - 3, 2, 1, p.brow);
        px(-1, hy - 2, 4, 1, p.brow);
        px(2, hy - 1, 2, 1, p.brow);
        px(8, hy - 3, 2, 1, p.brow);
        px(6, hy - 2, 4, 1, p.brow);
        px(5, hy - 1, 2, 1, p.brow);
      }
      if (angry) {
        px(3, hy, 1, 1, p.brow);
        px(5, hy, 1, 1, p.brow);
      }
      const h = style === 'refCold' ? 1 : angry ? 1 : 2;
      for (const x of [0, 6]) {
        const ix = x === 0 ? x + 1 : x;
        if (style === 'refCold') {
          px(x === 0 ? -1 : 6, ey + 1, 4, 1, lash);
          px(ix, ey + 2, 2, 1, irisC);
          px(x, ey + 3, 3, 1, p.skinS);
          continue;
        }
        px(x === 0 ? -1 : 6, ey, 4, 1, lash);
        px(x, ey + 1, 3, h, white);
        px(ix, ey + 1, 2, h, irisC);
        if (h === 2) px(ix + (x === 0 ? 1 : 0), ey + 1, 1, 1, p.evil ? '#ff9a9a' : '#ffffff');
        px(x, ey + 1 + h, 3, 1, p.skinS);
      }
      if (p.female) {
        px(-2, ey - 1, 1, 1, p.eye);
        px(10, ey - 1, 1, 1, p.eye);
      }
      px(4, hy + 4, 1, 2, p.skinS);
      px(-1, hy + 6, 1, 1, p.skinS);
      px(0, hy + 7, 1, 1, p.skinS);
      if (p.beard === 'long') {
        // Mouth hidden under the beard.
      } else if (style === 'refSerious') px(3, hy + 7, 3, 1, lip);
      else if (style === 'refSmirk') {
        px(3, hy + 7, 3, 1, lip);
        px(6, hy + 6, 1, 1, lip);
      } else if (style === 'refGrit') {
        px(2, hy + 7, 5, 2, lip);
        px(3, hy + 7, 3, 1, white);
      } else if (style === 'refShout') {
        px(2, hy + 6, 5, 3, '#8a2a2a');
        px(3, hy + 6, 3, 1, white);
        px(3, hy + 8, 3, 1, '#c04848');
      } else if (style === 'refSoft') {
        px(3, hy + 7, 2, 1, lip);
        px(5, hy + 6, 1, 1, lip);
      } else px(3, hy + 7, 2, 1, lip);
      break;
    }
    case 'fierceShadow':
    case 'fierceHawk':
    case 'fierceScar':
    case 'fierceGlare':
    case 'fierceHood': {
      // Intense, battle-hardened faces: heavy low brows, deep-set narrow eyes, hard mouth.
      const deep = '#1c7f8e';
      const deepH = '#3fb0b8';
      const hard = '#9a5048';
      const hawk = style === 'fierceHawk';
      if (style === 'fierceHood') px(-1, hy - 3, 11, 1, p.skinS);
      if (hawk) {
        px(-1, hy - 3, 1, 1, p.brow);
        px(0, hy - 2, 2, 1, p.brow);
        px(2, hy - 1, 2, 1, p.brow);
        px(9, hy - 3, 1, 1, p.brow);
        px(7, hy - 2, 2, 1, p.brow);
        px(5, hy - 1, 2, 1, p.brow);
      } else {
        px(-1, hy - 3, 2, 1, p.brow);
        px(-1, hy - 2, 4, 1, p.brow);
        px(2, hy - 1, 2, 1, p.brow);
        px(8, hy - 3, 2, 1, p.brow);
        px(6, hy - 2, 4, 1, p.brow);
        px(5, hy - 1, 2, 1, p.brow);
        // Shadow under the brow ridge makes the eyes deep-set.
        px(0, hy, 3, 1, p.skinS);
        px(6, hy, 3, 1, p.skinS);
      }
      for (const x of [0, 6]) {
        const outer = x === 0 ? -1 : 9;
        px(Math.min(x, outer), ey, 4, 1, p.eye);
        if (hawk) {
          px(x === 0 ? -2 : 10, ey - 1, 1, 1, p.eye);
          px(x, ey + 1, 3, 1, '#e0a830');
          px(x + 1, ey + 1, 1, 1, p.eye);
          px(x, ey + 2, 3, 1, p.skinS);
        } else if (style === 'fierceGlare') {
          px(x, ey + 1, 3, 1, '#f2f0ec');
          px(x + 1, ey + 1, 1, 1, p.eye);
          px(x + (x === 0 ? 2 : 0), ey + 1, 1, 1, iris);
          px(x, ey + 2, 3, 1, p.skinS);
        } else {
          px(x, ey + 1, 3, 2, deep);
          px(x + 1, ey + 1, 1, 2, p.eye);
          px(x, ey + 2, 3, 1, deepH);
          px(x + 1, ey + 2, 1, 1, p.eye);
          px(x, ey + 1, 1, 1, style === 'fierceHood' ? '#bff8ff' : W);
          px(x, ey + 3, 3, 1, p.skinS);
        }
      }
      px(4, hy + 5, 1, 1, p.skinS);
      if (style === 'fierceGlare') {
        // Gritted teeth.
        px(2, hy + 7, 5, 1, hard);
        px(3, hy + 7, 3, 1, '#f4f2ee');
        px(3, hy + 8, 3, 1, hard);
      } else if (hawk) {
        px(3, hy + 7, 3, 1, hard);
        px(2, hy + 8, 1, 1, hard);
        px(6, hy + 8, 1, 1, hard);
      } else px(3, hy + 7, 3, 1, hard);
      if (style === 'fierceScar') {
        // Old sword scar: nick through the brow, slash down the cheek.
        px(8, hy - 2, 1, 1, p.skin);
        px(8, hy - 3, 1, 1, p.skin);
        for (const [dx, dy] of [
          [8, 4],
          [9, 5],
          [9, 6],
          [10, 7],
        ] as const)
          px(dx, hy + dy, 1, 1, '#e7a092');
      }
      if (style === 'fierceHood') {
        line(g, cx + 4, hy - 3, cx + 3, hy + 2, p.hair);
        line(g, cx + 7, hy - 3, cx + 8, hy + 1, p.hair);
        px(-3, hy - 2, 2, 6, p.hair);
      }
      break;
    }
    case 'bold':
    case 'boldBig':
    case 'boldFierce':
    case 'boldSmile':
    case 'boldSharp':
    case 'boldFringe': {
      // Facing the viewer: two equal eyes (far at x 0..2, near at x 6..8) under furrowed V brows.
      const fierce = style === 'boldFierce';
      if (fierce) {
        px(-1, hy - 3, 2, 1, p.brow);
        px(-1, hy - 2, 3, 1, p.brow);
        px(1, hy - 1, 2, 1, p.brow);
        px(8, hy - 3, 2, 1, p.brow);
        px(6, hy - 2, 3, 1, p.brow);
        px(5, hy - 1, 2, 1, p.brow);
      } else {
        px(-1, hy - 2, 2, 1, p.brow);
        px(1, hy - 1, 2, 1, p.brow);
        px(5, hy - 1, 2, 1, p.brow);
        px(7, hy - 2, 2, 1, p.brow);
      }
      const tall = style === 'boldBig' ? 4 : style === 'boldSharp' || fierce ? 2 : 3;
      for (const x of [0, 6]) {
        const outer = x === 0 ? -1 : 9;
        px(x, ey, 3, 1, p.eye);
        if (style === 'boldBig') px(outer, ey, 1, 1, p.eye);
        if (style === 'boldSharp') px(outer, ey - 1, 1, 1, p.eye);
        px(x, ey + 1, 3, tall, iris);
        px(x + 1, ey + 1, 1, Math.max(2, tall - 1), p.eye);
        px(x, ey + tall, 3, 1, irisH);
        px(x, ey + 1, 1, 1, W);
        if (style === 'boldBig') px(x + 2, ey + 3, 1, 1, W);
        if (fierce) px(x, ey + tall + 1, 3, 1, p.skinS);
      }
      px(4, hy + 5, 1, 1, p.skinS);
      if (style === 'boldSmile') {
        px(2, hy + 6, 1, 1, p.mouth);
        px(6, hy + 6, 1, 1, p.mouth);
        px(3, hy + 7, 3, 1, p.mouth);
        px(-1, hy + 6, 2, 1, p.blush ?? '#f4b8aa');
        px(8, hy + 6, 2, 1, p.blush ?? '#f4b8aa');
      } else if (fierce) {
        px(3, hy + 7, 3, 1, p.mouth);
        px(2, hy + 8, 1, 1, p.mouth);
        px(6, hy + 8, 1, 1, p.mouth);
      } else if (style === 'boldBig') {
        px(3, hy + 8, 3, 1, p.mouth);
        px(6, hy + 7, 1, 1, p.mouth);
      } else px(3, hy + 7, 3, 1, p.mouth);
      if (style === 'boldFringe') {
        // A loose lock falling over the brow between the eyes, and a longer side bang.
        line(g, cx + 4, hy - 3, cx + 3, hy + 2, p.hair);
        line(g, cx + 5, hy - 3, cx + 4, hy + 1, p.hair);
        px(-3, hy - 2, 2, 6, p.hair);
        px(10, hy - 2, 1, 4, p.hair);
      }
      break;
    }
  }
}

/**
 * Body proportions: the figure was designed about 2.5 heads tall; these extra pixels lengthen the
 * torso (neck to waist) and the legs, making it about 3.5 heads tall with the head unchanged.
 */
export const TORSO_EXTRA = 8;
export const LEG_EXTRA = 16;
export const BODY_EXTRA = TORSO_EXTRA + LEG_EXTRA;
/**
 * Empty rows above the head so raised swords fit inside the frame, and the grown body (its head
 * rides higher) keeps its topknot inside it.
 */
export const PAD_TOP = 16;
/** Feet anchor column; wide margins on both sides leave room for swings and thrusts. */
export const ART_CX = 48;

export const FAR_HAND: Record<Exclude<FarArm, 'hang'>, [number, number]> = {
  back: [-11, 33],
  // Over the rear shoulder to a sword on the back (F2): as `back` for now.
  overBack: [-11, 33],
  fwd: [9, 31],
  up: [-10, 13],
  clasp: [3, 30],
  // The classic figure has no arms against the body: near enough positions for it.
  hip: [-8, 37],
  fold: [3, 31],
  face: [3, 16],
  clap: [7, 29],
  clapOpen: [5, 29],
  // The off hand hanging by the hip holding a reversed blade (motions/hold.ts `revArm`).
  revArm: [1, 38],
};

/** A tapering limb (sleeve, leg) as a chain of square dabs, with a shade edge underneath. */
const rawLimb = (...a: Parameters<typeof limb>) => limb(...a);

function limb(
  g: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  w0: number,
  w1: number,
  fill: string,
  shade: string,
): void {
  const n = Math.max(1, Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (const pass of [0, 1])
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = Math.round(x0 + (x1 - x0) * t);
      const y = Math.round(y0 + (y1 - y0) * t);
      const w = Math.max(1, Math.round(w0 + (w1 - w0) * t));
      const h = Math.floor(w / 2);
      box(g, x - h, y - h + (pass ? 0 : 1), w, w, pass ? fill : shade);
    }
}

type Mood = 'idle' | 'atk' | 'block' | 'cast' | 'closed';
const LASH = '#1a1010';
const LIP = '#7a3a32';

/** Mouth for a mood, `w` wide at (x, y); shouting opens it, gritting bares the teeth. */
function mouth(g: CanvasRenderingContext2D, p: Look, x: number, y: number, w: number, mood: Mood): void {
  const white = '#f6f2ec';
  if (mood === 'atk') {
    box(g, x - 1, y - 1, w + 2, 3, '#8a2a2a');
    box(g, x, y - 1, w, 1, white);
    box(g, x, y + 1, w, 1, '#c04848');
  } else if (mood === 'block' || mood === 'cast') {
    box(g, x - 1, y, w + 2, 2, LIP);
    box(g, x, y, w, 1, white);
  } else box(g, x, y, mood === 'closed' ? w - 1 : w, 1, p.beard === 'long' ? (p.beardColor ?? LIP) : LIP);
}

/**
 * A face: the rounded skull and cheeks from an ellipse cut off at `jawY`, then the jaw below as
 * explicit rows `[from, to]` (relative to `x0`), so the chin can taper to a V instead of a circle.
 */
function faceShape(
  g: CanvasRenderingContext2D,
  p: Look,
  ex: number,
  ey: number,
  rx: number,
  ry: number,
  x0: number,
  jawY: number,
  jaw: readonly (readonly [number, number])[],
): void {
  g.save();
  g.beginPath();
  g.rect(-200, -200, 600, jawY + 200);
  g.clip();
  ell(g, ex, ey, rx, ry, p.skin);
  g.restore();
  jaw.forEach(([a, b], i) => box(g, x0 + a, jawY + i, b - a + 1, 1, p.skin));
}

const rgbOf = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const hexOf = (c: readonly number[]) =>
  `#${c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

/**
 * Colour of the strand lines that split hair into locks: a shade darker on mid and light hair,
 * but toward the highlight on near-black hair, where a darker line would not show.
 */
function strand(p: Look): string {
  const c = rgbOf(p.hair);
  const lum = (0.3 * c[0] + 0.59 * c[1] + 0.11 * c[2]) / 255;
  if (lum < 0.16) {
    const h = rgbOf(p.hairH);
    return hexOf(c.map((v, i) => v + (h[i]! - v) * 0.45));
  }
  const k = lum > 0.7 ? 0.2 : 0.38;
  return hexOf(c.map((v) => v * (1 - k)));
}

/**
 * Ink lines between body parts (face, hair, robe, sash, coat, weapon…), so each part of a figure
 * reads on its own. A switch, so the look can be compared with and without them.
 */
let partLines = false;
export function setPartLines(on: boolean): void {
  partLines = on;
}

/**
 * Which part each of a look's colours belongs to. Where two parts meet, the line goes on the
 * lighter side so it always shows (a face against dark hair gets its own edge). Colours not listed
 * (eyes, mouth, trims, tassels, crowns, the narrow inner collar) are details and never take a line,
 * so small pieces keep their colour.
 */
function partDepths(p: Look): Map<number, number> {
  const lift = (hex: string, k: number) => hexOf(rgbOf(hex).map((v) => v + (255 - v) * k));
  const parts: [number, (string | undefined)[]][] = [
    [10, [p.skin, p.skinS]],
    [9, [p.blade, p.bladeS, p.hilt, p.guard, p.scabbard]],
    [8, [p.sash, p.sashS, lift(p.sash, 0.22)]],
    [7, [p.hat?.c, p.hat?.s]],
    [6, [p.hair, p.hairH, strand(p)]],
    [5, [p.stole?.c, p.stole?.s, p.pelt?.c, p.pelt?.s, p.pauldron?.c, p.pauldron?.s]],
    [4, [p.coat?.c, p.coat?.s, p.coat ? lift(p.coat.c, 0.16) : undefined]],
    [3, [p.outer, p.outerS, p.outerH, p.motif]],
    [1, [p.boots, p.bootsS]],
    [0, [p.cape, p.capeS]],
  ];
  const map = new Map<number, number>();
  for (const [depth, cols] of parts)
    for (const hex of cols) {
      if (!hex || hex[0] !== '#') continue;
      const key = parseInt(hex.slice(1, 7), 16);
      if (!map.has(key)) map.set(key, depth);
    }
  return map;
}

function drawPartLines(c: HTMLCanvasElement, p: Look): void {
  const g = ctx2d(c);
  const { width: w, height: h } = c;
  const img = g.getImageData(0, 0, w, h);
  const d = img.data;
  const depths = partDepths(p);
  const depth = new Int8Array(w * h).fill(-1);
  for (let i = 0; i < w * h; i++)
    if (d[i * 4 + 3]) depth[i] = depths.get((d[i * 4]! << 16) | (d[i * 4 + 1]! << 8) | d[i * 4 + 2]!) ?? -1;
  const ink = rgbOf('#18121e');
  const lum = (i: number) => 0.3 * d[i * 4]! + 0.59 * d[i * 4 + 1]! + 0.11 * d[i * 4 + 2]!;
  const line = new Uint8Array(w * h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const a = depth[y * w + x]!;
      if (a < 0) continue;
      for (const [nx, ny] of [
        [x + 1, y],
        [x, y + 1],
      ] as const) {
        if (nx >= w || ny >= h) continue;
        const b = depth[ny * w + nx]!;
        if (b < 0 || b === a) continue;
        line[lum(y * w + x) >= lum(ny * w + nx) ? y * w + x : ny * w + nx] = 1;
      }
    }
  for (let i = 0; i < w * h; i++) {
    if (!line[i]) continue;
    // A dark shade of the part's own colour, so lines read as ink without going flat black.
    for (let k = 0; k < 3; k++) d[i * 4 + k] = d[i * 4 + k]! * 0.35 + ink[k]! * 0.65;
  }
  g.putImageData(img, 0, 0);
}

/**
 * Mouth in profile, `x` being the front edge of the face on row `y`: a dark stroke in the same ink
 * as the eye line, drawn inside that edge (on the outline itself it juts out like buck teeth).
 */
function sideMouth(g: CanvasRenderingContext2D, x: number, y: number, mood: Mood): void {
  if (mood === 'atk') box(g, x - 3, y - 1, 2, 2, LASH);
  else if (mood === 'block' || mood === 'cast') box(g, x - 3, y, 3, 1, LASH);
  else box(g, x - 2, y, mood === 'closed' ? 1 : 2, 1, LASH);
}

/** Face seen straight on, centred on `mx`: heavy V brows, two eyes looking at the camera. */
function frontFace(g: CanvasRenderingContext2D, p: Look, mx: number, hy: number, mood: Mood): void {
  faceShape(g, p, mx, hy + 2.5, 6.8, 7, mx, hy + 6, [
    [-5, 5],
    [-4, 4],
    [-3, 3],
    [-2, 2],
    [-1, 1],
  ]);
  box(g, mx - 7, hy + 1, 1, 4, p.skinS);
  box(g, mx + 7, hy + 1, 1, 4, p.skinS);
  // Fringe.
  box(g, mx - 7, hy - 7, 15, 3, p.hair);
  if (p.hairStyle === 'curtain' || p.hairStyle === 'buns' || p.hairStyle === 'loose') {
    // Centre-parted heavy bangs sweeping out to both sides, long locks framing the cheeks.
    for (let r = 0; r < 4; r++) {
      const w = Math.max(1, 5 - r * 1.5);
      box(g, mx - 7, hy - 4 + r, Math.round(w), 1, p.hair);
      box(g, mx + 8 - Math.round(w), hy - 4 + r, Math.round(w), 1, p.hair);
    }
    box(g, mx - 2, hy - 4, 2, 1, p.hair);
    box(g, mx + 1, hy - 4, 2, 1, p.hair);
    box(g, mx - 8, hy - 2, 2, 9, p.hair);
    box(g, mx + 7, hy - 2, 2, 9, p.hair);
    line(g, mx - 4, hy - 6, mx - 2, hy - 4, p.hairH);
    line(g, mx + 4, hy - 6, mx + 2, hy - 4, p.hairH);
  } else if (p.hairStyle === 'bun' || p.hairStyle === 'messy') {
    for (const x of [-6, -3, 1, 4]) {
      box(g, mx + x, hy - 4, 2, 1, p.hair);
      box(g, mx + x + (x < 0 ? 0 : 1), hy - 3, 1, 1, p.hair);
    }
    box(g, mx - 8, hy - 4, 1, 3, p.hair);
    box(g, mx + 8, hy - 4, 1, 3, p.hair);
  } else {
    box(g, mx - 7, hy - 4, 15, 1, p.hair);
    box(g, mx - 8, hy - 3, 2, 8, p.hair);
    box(g, mx + 7, hy - 3, 2, 8, p.hair);
  }
  // Parting and strand lines running back over the crown.
  const hs = strand(p);
  box(g, mx, hy - 8, 1, 3, hs);
  line(g, mx - 5, hy - 8, mx - 7, hy - 5, hs);
  line(g, mx + 5, hy - 8, mx + 7, hy - 5, hs);
  // Eyes and brows.
  const angry = mood === 'atk' || mood === 'block' || mood === 'cast';
  const iris = p.iris ?? p.eye;
  const white = p.evil ? '#e6dce8' : '#f6f2ec';
  for (const side of [-1, 1]) {
    const ex = side < 0 ? mx - 4 : mx + 2;
    const out = side < 0 ? ex - 2 : ex + 3;
    const inn = side < 0 ? ex + 2 : ex - 1;
    box(g, side < 0 ? out : ex, hy - 1, 4, 1, p.brow);
    box(g, inn, hy, 1, 1, p.brow);
    if (angry) box(g, side < 0 ? inn - 1 : inn + 1, hy, 1, 1, p.brow);
    if (mood === 'closed') {
      box(g, ex, hy + 3, 3, 1, LASH);
      continue;
    }
    const h = angry ? 1 : 2;
    box(g, ex - (side < 0 ? 1 : 0), hy + 1, 4, 1, LASH);
    box(g, ex, hy + 2, 3, h, white);
    box(g, side < 0 ? ex + 1 : ex, hy + 2, 2, h, iris);
    if (h === 2) box(g, side < 0 ? ex + 2 : ex + 1, hy + 2, 1, 1, p.evil ? '#ff9a9a' : '#ffffff');
    box(g, ex, hy + 2 + h, 3, 1, p.skinS);
    if (p.female) box(g, out, hy, 1, 1, LASH);
  }
  box(g, mx, hy + 4, 1, 2, p.skinS);
  if (p.beard !== 'long') mouth(g, p, mx - 1, hy + 7, 3, mood);
  if (p.beard === 'stubble')
    for (const [x, y] of [
      [-3, 7],
      [-2, 8],
      [2, 8],
      [3, 7],
    ] as const)
      box(g, mx + x, hy + y, 1, 1, '#8a6248');
  if (p.beard === 'long') {
    const b = p.beardColor ?? '#eeeeee';
    box(g, mx - 3, hy + 6, 7, 2, b);
    box(g, mx - 3, hy + 8, 7, 3, b);
    box(g, mx - 2, hy + 11, 5, 4, b);
    box(g, mx - 1, hy + 15, 3, 3, b);
    box(g, mx - 1, hy + 6, 3, 1, p.mouth);
  }
  if (p.mask) {
    box(g, mx - 5, hy + 4, 11, 3, p.mask);
    box(g, mx - 4, hy + 7, 9, 1, p.mask);
    box(g, mx - 2, hy + 8, 5, 1, p.mask);
    box(g, mx - 1, hy + 9, 3, 1, p.mask);
  }
}

/** Face in profile, looking screen-right: one eye, nose tip and mouth on the front edge, ear in the hair. */
function sideFace(g: CanvasRenderingContext2D, p: Look, cx: number, hy: number, mood: Mood): void {
  // Jaw sloping up from a forward chin to the ear, so the profile reads slim rather than round.
  faceShape(g, p, cx + 6.5, hy + 2.5, 5, 7, cx, hy + 6, [
    [2, 10],
    [4, 10],
    [5, 9],
    [6, 9],
    [7, 8],
  ]);
  box(g, cx + 11, hy + 3, 1, 2, p.skin);
  box(g, cx + 11, hy + 5, 1, 1, p.skinS);
  // Ear, half hidden by hair.
  box(g, cx + 1, hy + 2, 2, 3, p.skin);
  box(g, cx + 1, hy + 3, 1, 1, p.skinS);
  box(g, cx + 3, hy + 6, 1, 1, p.skinS);
  // Fringe sweeping forward over the brow.
  box(g, cx - 3, hy - 6, 14, 3, p.hair);
  for (let r = 0; r < 3; r++) box(g, cx + 6 + r, hy - 3 + r, 4 - r, 1, p.hair);
  if (p.hairStyle === 'curtain' || p.hairStyle === 'buns' || p.hairStyle === 'loose')
    box(g, cx + 3, hy - 3, 2, 10, p.hair);
  line(g, cx + 1, hy - 5, cx + 4, hy - 4, p.hairH);
  const hs = strand(p);
  line(g, cx - 4, hy - 6, cx - 6, hy + 3, hs);
  line(g, cx - 1, hy - 8, cx - 2, hy, hs);
  line(g, cx + 5, hy - 6, cx + 7, hy - 4, hs);
  // Eye, brow, mouth.
  const angry = mood === 'atk' || mood === 'block' || mood === 'cast';
  box(g, cx + 5, hy - 1, 4, 1, p.brow);
  if (angry) box(g, cx + 8, hy, 2, 1, p.brow);
  if (mood === 'closed') box(g, cx + 6, hy + 3, 3, 1, LASH);
  else {
    const h = angry ? 1 : 2;
    box(g, cx + 6, hy + 1, 4, 1, LASH);
    box(g, cx + 6, hy + 2, 3, h, p.evil ? '#e6dce8' : '#f6f2ec');
    box(g, cx + 7, hy + 2, 2, h, p.iris ?? p.eye);
    box(g, cx + 6, hy + 2 + h, 3, 1, p.skinS);
  }
  if (p.beard !== 'long') sideMouth(g, cx + 10, hy + 7, mood);
  if (p.beard === 'stubble')
    for (const [x, y] of [
      [5, 7],
      [6, 8],
      [7, 9],
    ] as const)
      box(g, cx + x, hy + y, 1, 1, '#8a6248');
  if (p.beard === 'long') {
    const b = p.beardColor ?? '#eeeeee';
    box(g, cx + 6, hy + 6, 5, 2, b);
    box(g, cx + 5, hy + 8, 5, 4, b);
    box(g, cx + 5, hy + 12, 3, 4, b);
    box(g, cx + 5, hy + 16, 2, 2, b);
  }
  if (p.mask) {
    box(g, cx + 6, hy + 4, 6, 4, p.mask);
    box(g, cx + 6, hy + 8, 4, 1, p.mask);
  }
}

/**
 * Classic wuxia martial artist, facing screen-right (mirrored at draw time).
 * Layered back to front: cape, hair, far sleeve, legs, robe skirt, weapon, torso, near sleeve, head.
 * The upper body is shifted by `lean` while the legs stay planted.
 * `view` picks one of five camera angles; mirrored at draw time they cover eight directions.
 * In the back views the face turns away, the long hair falls over the back, and the robe shows its
 * closed back panel. The straight front and back views are drawn symmetrically.
 */
/** Secondary motion baked into a frame: extra fabric lag and a flutter phase for hair and ribbons. */
export interface Cloth {
  sway: number;
  flutter: number;
}
const STILL: Cloth = { sway: 0, flutter: 0 };

export function martialArtist(
  p: Look,
  pose: Pose,
  ph = 0,
  view: View = 'front3',
  cloth = STILL,
): HTMLCanvasElement {
  const back = view === 'back3' || view === 'back';
  /** Straight-on views (front or back): symmetric body, arms at both sides, feet side by side. */
  const square = view === 'front' || view === 'back';
  const T = p.tall ?? 0;
  const c = canvas(ART_CX * 2 + 8, 60 + T + PAD_TOP + BODY_EXTRA);
  const g = ctx2d(c);
  const cx = ART_CX;
  const TX = TORSO_EXTRA;
  const FY = 57 + T + BODY_EXTRA; // ground line
  /** The ground in upper-body design coordinates (see `Y`). */
  const FYu = FY - TX;
  const rig = rigFor(pose, ph);
  // Fabric still swinging from earlier movement: lag adds to the pose's own sway and flares the hem.
  rig.sway += cloth.sway;
  rig.flare += Math.abs(cloth.sway) * 0.35;
  // Poses were keyed for short legs: longer legs take longer strides and lift their feet higher.
  const LS = 1 + LEG_EXTRA / 27;
  for (const k of ['f1', 'f2', 'l1', 'l2', 'hem'] as const) rig[k] = Math.round(rig[k] * LS);
  if (square) Object.assign(rig, { f1: 0, f2: 0 });
  // Facing the camera (or away), forward motion is toward the viewer: no sideways lean or stride.
  if (square) Object.assign(rig, { lean: 0, headDx: 0, f1: 0, f2: 0 });
  /** Straight-on views hold the near arm out at the side of the torso instead of across it. */
  const armX = square ? 2 : 0;
  const sit = isSeated(pose);
  const o = rig.o + (sit ? T + LEG_EXTRA : 0);
  /**
   * The upper body is designed at the original proportions and drawn through `Y`: the head stays put,
   * the band from neck to waist is stretched by TX pixels, and anything below the waist moves down
   * by TX. Every primitive maps whole pixels, so lines and edges stay crisp.
   */
  let mapped = false;
  const band0 = 24 + o;
  const band1 = 37 + o;
  const sc = (band1 - band0 + TX) / (band1 - band0);
  const Y = (y: number) => (!mapped || y <= band0 ? y : y <= band1 ? band0 + (y - band0) * sc : y + TX);
  const box = (gg: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, col: string) => {
    if (!mapped) return rawBox(gg, x, y, w, h, col);
    const y0 = Math.round(Y(y));
    rawBox(gg, x, y0, w, Math.max(1, Math.round(Y(y + h)) - y0), col);
  };
  const line = (gg: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, col: string) =>
    rawLine(gg, x0, Math.round(Y(y0)), x1, Math.round(Y(y1)), col);
  const ell = (gg: CanvasRenderingContext2D, ex: number, ey: number, rx: number, ry: number, col: string) =>
    rawEll(gg, ex, Y(ey), rx, mapped && ey > band0 && ey < band1 ? ry * sc : ry, col);
  const limb = (
    gg: CanvasRenderingContext2D,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    w0: number,
    w1: number,
    fill: string,
    shade: string,
  ) => rawLimb(gg, x0, Math.round(Y(y0)), x1, Math.round(Y(y1)), w0, w1, fill, shade);
  const { sway, lean } = rig;
  const upper = (dx = 0) => {
    mapped = true;
    g.setTransform(1, 0, 0, 1, lean + dx, PAD_TOP);
  };
  const lower = () => {
    mapped = false;
    g.setTransform(1, 0, 0, 1, 0, PAD_TOP);
  };
  const wave = ph * 1.6 + cloth.flutter * 1.4;
  /** The waist in upper-body design coordinates, and where the skirt starts on the canvas. */
  const waist = 37 + o;
  const skirtTop = waist + TX;
  const hanfu = !!p.hanfu && !p.shortRobe;
  const skirtBot = p.shortRobe ? 46 + o + TX : FY - (hanfu ? 6 : 4) - rig.hem;
  const carries = p.weapon === 'sword' || p.weapon === 'sabre';
  const armed = carries && !p.sheathed;
  const lim = LIMB[rig.arm];

  /** A sword or sabre held at (hx, hy), pointing along `ang`; stops at the ground. */
  const blade = (hx: number, hy: number, ang: number) => {
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    const L = dy > 0 ? Math.min(p.wlen, (FYu - 1 - hy) / dy) : p.wlen;
    let nx = Math.round(-dy) || 0;
    let ny = Math.round(dx) || 0;
    if (ny < 0 || (ny === 0 && nx < 0)) [nx, ny] = [-nx, -ny];
    if (nx && ny) ny = 0;
    const rx = Math.round(dx) || 0;
    const ry = Math.round(dy) || 0;
    const bx = Math.round(hx + dx * 2);
    const by = Math.round(hy + dy * 2);
    const tx = Math.round(hx + dx * L);
    const ty = Math.round(hy + dy * L);
    line(g, hx - rx * 3, hy - ry * 3, hx, hy, p.hilt);
    line(g, bx, by, tx, ty, p.blade);
    line(g, bx + nx, by + ny, tx + nx - rx, ty + ny - ry, p.bladeS);
    if (p.weapon === 'sabre')
      line(g, bx + nx * 2, by + ny * 2, tx + nx * 2 - rx * 3, ty + ny * 2 - ry * 3, p.blade);
    const gx = hx + rx;
    const gy = hy + ry;
    line(
      g,
      gx - Math.round(-dy * 2),
      gy - Math.round(dx * 2),
      gx + Math.round(-dy * 2),
      gy + Math.round(dx * 2),
      p.guard,
    );
    if (p.weapon === 'sword') box(g, hx - rx * 3, hy - ry * 3 + 1, 1, 3, p.tassel);
  };

  // ── cape ──
  upper();
  if (p.cape && !sit) {
    for (let y = 24 + o; y <= FYu - 3 - rig.hem; y++) {
      const k = (y - 24 - o) / (FYu - 27 - o);
      const x0 = Math.round(cx - 7 - k * 6 - k * k * sway * 2 - k * lean);
      const x1 = Math.round(cx + 2 - k - k * lean);
      box(g, x0, y, x1 - x0, 1, p.cape);
      box(g, x0, y, 2, 1, p.capeS!);
    }
  }

  // ── hair behind the body (over the back in the back view) ──
  const backHair = () => {
    if (view === 'front') {
      // Head-on, a ponytail, bun strands and ribbons hide behind the head; long loose hair falls
      // symmetrically behind both shoulders.
      upper(rig.headDx);
      if (p.hairStyle === 'loose' || p.hairStyle === 'buns') {
        const len = (p.hairStyle === 'loose' ? 30 : 20) + Math.round(T * 0.8);
        for (let r = 0; r < len; r++) {
          const y = 10 + o + r;
          if (sit && y > FYu - 2) break;
          const hw = Math.round(10 - (r / len) * 2);
          box(g, cx + 1 - hw, y, hw * 2 + 1, 1, p.hair);
          if (r % 5 === 2) {
            box(g, cx + 2 - hw, y, 1, 1, p.hairH);
            box(g, cx + hw, y, 1, 1, p.hairH);
          }
          if (r % 4) {
            box(g, cx + 4 - hw, y, 1, 1, strand(p));
            box(g, cx + hw - 2, y, 1, 1, strand(p));
          }
        }
      }
      return;
    }
    upper(rig.headDx + (view === 'back' ? 7 : back ? 6 : view === 'side' ? -1 : 0));
    if (p.hairStyle === 'ponytail' || p.hairStyle === 'curtain') {
      for (let r = 0; r < 26 + Math.round(T * 0.8); r++) {
        const y = 7 + o + r;
        if (sit && y > FYu - 2) break;
        const x = Math.round(
          cx -
            6 -
            r * (back ? 0.1 : 0.32) +
            Math.sin(r * 0.28 + wave + sway) * 1.4 -
            (r / 26) * Math.max(0, sway - 1) * 4,
        );
        const w = Math.max(1, Math.round((back ? 5 : 4) - r * 0.11));
        box(g, x - w, y, w, 1, p.hair);
        if (w >= 3) box(g, x - Math.ceil(w / 2), y, 1, 1, strand(p));
        if (r % 6 === 2) box(g, x - w + 1, y, 1, 1, p.hairH);
      }
    } else if (p.hairStyle === 'loose' || p.hairStyle === 'buns') {
      // A continuous curtain of hair down the back, swaying as one mass.
      const len = (p.hairStyle === 'loose' ? 32 : 22) + Math.round(T * 0.8);
      for (let r = 0; r < len; r++) {
        const y = 8 + o + r;
        if (sit && y > FYu - 2) break;
        const k = r / len;
        const x1 = Math.round(cx - 2 - k * 5 + Math.sin(k * 3 + wave * 0.6) * k * 1.5 - k * sway);
        const w = Math.max(2, Math.round(9 - k * 5));
        box(g, x1 - w, y, w, 1, p.hair);
        // Two dark strand lines running the length of the hair, splitting it into locks.
        if (r % 3) box(g, x1 - Math.round(w * 0.5), y, 1, 1, strand(p));
        if (k > 0.25 && w > 4 && r % 4) box(g, x1 - w + 2, y, 1, 1, strand(p));
        if (r % 5 === 1) box(g, x1 - w + 1, y, 1, 1, p.hairH);
        if (r % 7 === 4) box(g, x1 - 2, y, 1, 1, p.hairH);
      }
    }
    if (p.hairStyle === 'bun') {
      // Two long loose strands escaping the bun and drifting behind.
      for (const [off, len] of [
        [0, 22],
        [3, 14],
      ] as const)
        for (let r = 0; r < len; r++) {
          const x = Math.round(
            cx - 5 - off - r * (0.8 + sway * 0.15) + Math.sin(r * 0.35 + wave + off) * 1.6,
          );
          const y = Math.round(5 + o + off + r);
          box(g, x, y, 1, 1, p.hair);
        }
    }
    const ribbon = p.ribbon ?? (p.headwear === 'guan' || p.headwear === 'band' ? p.accent : undefined);
    if (ribbon) {
      // Two ribbons trailing from the crown, topknot or headband knot.
      const sy = p.headwear === 'band' ? 10 : 5;
      const long = p.ribbon ? 6 : 0;
      for (const [off, len] of [
        [0, 15 + long],
        [2, 12 + long],
      ] as const)
        for (let r = 0; r < len; r++) {
          const x = Math.round(
            cx - 6 - r * (0.85 + sway * 0.12) - off + Math.sin(r * 0.5 + wave + off) * 1.2,
          );
          const y = Math.round(sy + o + r * 0.9 + off);
          box(g, x, y, off ? 1 : 2, 1, ribbon);
        }
    }
  };
  if (!back) backHair();

  // ── far sleeve ──
  upper();
  if (rig.far !== 'hang') {
    const [hx, hy] = FAR_HAND[rig.far];
    const fill = p.sleeveless ? p.skinS : p.outerS;
    limb(g, cx - 3, 26 + o, cx + hx, hy + o, 3, 4, fill, fill);
    box(g, cx + hx - 1, hy + o - 1, 2, 2, p.skinS);
  } else if (view === 'side') {
    // Profile: the far arm is hidden behind the body.
  } else if (p.sleeveless) {
    box(g, cx - 7 - (square ? 1 : 0), 25 + o, 2, 10, square ? p.skin : p.skinS);
    box(g, cx - 7 - (square ? 1 : 0), 33 + o, 2, 2, p.inner);
    if (square) box(g, cx - 8, 35 + o, 2, 2, p.skin);
  } else if (hanfu) {
    // A bell sleeve hanging from the far shoulder, its open cuff showing the lining.
    let cuff = cx;
    for (let y = 25 + o; y <= 39 + o; y++) {
      const k = (y - 25 - o) / 14;
      const w = Math.round(3 + k * k * 6);
      const x1 =
        cx - 4 - (square ? 2 : 0) - Math.round(k * (sway - rig.armSwing) + k * 3.5) + Math.round(k * k * 2);
      const col = y === 39 + o ? p.trim : y === 38 + o ? p.inner : square ? p.outer : p.outerS;
      box(g, x1 - w, y, w, 1, col);
      if (y < 38 + o) {
        box(g, x1 - w, y, 1, 1, p.outerS);
        box(g, x1 - 1, y, 1, 1, p.outerS);
        if (square && k > 0.4) box(g, x1 - Math.round(w * 0.5), y, 1, 1, p.outerS);
      }
      cuff = x1 - Math.round(w / 2);
    }
    // Head-on, the far hand shows below its cuff, mirroring the near one.
    if (square) box(g, cuff - 1, 40 + o, 2, 2, p.skin);
  } else {
    let cuff = cx;
    for (let y = 25 + o; y <= 38 + o; y++) {
      const w = Math.round(3 + ((y - 25 - o) / 13) * 3);
      const x1 = cx - 4 - (square ? 2 : 0) - Math.round(((y - 25 - o) / 13) * (sway - rig.armSwing));
      box(g, x1 - w, y, w, 1, y === 38 + o ? p.trim : square ? p.outer : p.outerS);
      if (square) box(g, x1 - w, y, 1, 1, p.outerS);
      cuff = x1;
    }
    // Head-on, the far hand shows below its cuff, mirroring the near one.
    if (square) box(g, cuff - 4, 38 + o, 2, 2, p.skin);
  }

  // ── scabbard at the far hip (behind the legs in the front views) ──
  const scabbard = (x0: number, y0: number, x1: number, y1: number) => {
    if (!p.scabbard || !carries || sit) return;
    line(g, x0, y0, x1, y1, p.scabbard);
    line(g, x0, y0 + 1, x1, y1 + 1, p.scabbard);
    box(g, x0 - 1, y0 - 1, 2, 3, p.guard);
    box(g, x1 - 1, y1 - 1, 2, 3, p.guard);
    const mx = Math.round((x0 + x1) / 2);
    box(g, mx, Math.round((y0 + y1) / 2), 1, 2, p.guard);
    if (p.sheathed) {
      // The sword home in its scabbard: guard and hilt jutting up past the mouth, tassel hanging.
      const [dx, dy] = [Math.sign(x0 - x1) || 1, -1];
      box(g, x0 - 2, y0 - 2, 4, 1, p.guard);
      line(g, x0, y0 - 2, x0 + dx * 4, y0 - 2 + dy * 5, p.hilt);
      box(g, x0 + dx * 4, y0 - 3 + dy * 5, 2, 2, p.guard);
      box(g, x0 + dx * 5, y0 - 1 + dy * 5, 1, 3, p.tassel);
    }
  };
  upper();
  if (view === 'front') scabbard(cx - 6, 36 + o, cx - 14, 46 + o);
  else if (!back) scabbard(cx - 3, 36 + o, cx - 19, 46 + o);

  // ── legs ──
  lower();
  if (sit) {
    ell(g, cx + 1, FY - 3, 12, 3.5, p.outerS);
    ell(g, cx + 2, FY - 4, 9, 2.5, p.outer);
    box(g, cx - 4, FY - 2, 4, 2, p.boots);
    box(g, cx + 6, FY - 2, 4, 2, p.boots);
    box(g, cx - 11, FY - 1, 24, 1, p.trim);
  } else {
    const legs = [
      { hip: cx - 3, foot: cx - 6 + rig.f1, lift: rig.l1, w: 5, col: p.shortRobe ? p.inner : p.innerS },
      { hip: cx + 3, foot: cx + 1 + rig.f2, lift: rig.l2, w: 6, col: p.shortRobe ? p.innerS : p.innerS },
    ];
    for (const L of legs) {
      const fy = FY - 3 - L.lift;
      limb(g, L.hip + lean, skirtTop + 3, L.foot + 2, fy - 1, 4, 3, L.col, p.innerS);
      if (p.shortRobe)
        // Leg wraps.
        for (let y = fy - 5; y < fy; y += 2) box(g, L.foot, y, 4, 1, p.outerH);
      if (hanfu) {
        // Tall boots up to the shin, showing below the short hanfu hem.
        box(g, L.foot, fy - 2, L.w, 5, p.boots);
        box(g, L.foot + 1, fy - 2, 1, 3, p.bootsS);
      } else box(g, L.foot, fy, L.w, 3, p.boots);
      box(g, L.foot, fy + 2, L.w, 1, p.bootsS);
    }
  }

  /**
   * The hanfu skirt: an A-line from the sash to a hem about three times as wide, its front panels
   * parting over the inner skirt, folds fanning out from the waist and the hem's corners dipping.
   * Three-quarter views trail more cloth on the back side; walking sways the hem.
   */
  function hanfuSkirt(): void {
    const span = Math.max(1, skirtBot - skirtTop);
    const spread = Math.abs(rig.f2 - rig.f1) * 0.3;
    /** Half-width of the hem before flare: widest head-on, narrowest in profile. */
    const hem = square ? 16 : view === 'side' ? 12 : 14;
    for (let y = skirtTop; y <= skirtBot + 1; y++) {
      const k = Math.min(1, (y - skirtTop) / span);
      const hw = Math.round(7 + (hem - 7) * (0.45 * k + 0.55 * k * k) + k * k * (rig.flare + spread));
      const hs = Math.round(lean * (1 - k) + k * k * -sway * 1.8);
      // Trailing side (away from the facing) carries more cloth.
      const L = cx + hs - (square ? hw : Math.round(hw * 1.15));
      const R = cx + hs + (square ? hw : Math.round(hw * 0.85));
      const W = R - L + 1;
      if (y > skirtBot) {
        // The hem's corners hang a row lower than its middle.
        box(g, L, y, 3, 1, p.trim);
        box(g, R - 2, y, 3, 1, p.trim);
        continue;
      }
      const edge = y >= skirtBot - 1;
      box(g, L, y, W, 1, edge ? p.trim : p.outer);
      if (edge) continue;
      box(g, L, y, 2, 1, p.outerS);
      if (!back) box(g, R, y, 1, 1, p.outerS);
      if (y === skirtBot - 2) box(g, L, y, W, 1, p.outerS);
      /** Folds fanning out from the waist: a shade line with a lit edge on alternate rows. */
      const fold = (f: number, lit: boolean) => {
        if (k < 0.2) return;
        const x = L + Math.round(W * f);
        box(g, x, y, 1, 1, p.outerS);
        if (lit && y % 2) box(g, x + 1, y, 1, 1, p.outerH);
      };
      if (back) {
        box(g, cx + hs + (view === 'back' ? 0 : 1), y, 1, 1, p.outerS);
        fold(0.22, true);
        fold(0.78, true);
      } else {
        // The opening: centred head-on, toward the facing side in three-quarter and profile.
        const mid = square ? cx + hs : cx + hs + 2 + Math.round(k * 2);
        const gap = Math.round(k * (square ? 4 : view === 'side' ? 2 : 3));
        box(g, mid - gap, y, gap * 2 + 1, 1, p.inner);
        if (gap > 1) box(g, mid - gap + 1, y, 1, 1, p.innerS);
        if (gap > 2 && y % 3) box(g, mid + 1, y, 1, 1, p.innerS);
        box(g, mid - gap - 1, y, 1, 1, p.trim);
        box(g, mid + gap + 1, y, 1, 1, p.trim);
        fold(square ? 0.18 : 0.12, true);
        fold(square ? 0.33 : 0.3, false);
        if (square) {
          fold(0.67, false);
          fold(0.82, true);
        } else fold(0.85, true);
      }
      if (y === skirtTop) box(g, L, y, W, 1, p.outerS); // shadow under the sash
      if (p.motif && y % 4 === 1 && k > 0.5)
        for (let x = L + 2; x < R - 1; x += 4) box(g, x + ((y >> 2) % 2) * 2, y, 1, 1, p.motif);
    }
  }

  // ── robe skirt: back panel + front flap, open at the front to show the inner robe ──
  if (!sit && hanfu) hanfuSkirt();
  else if (!sit) {
    const span = Math.max(1, skirtBot - skirtTop);
    for (let y = skirtTop; y <= skirtBot; y++) {
      const k = (y - skirtTop) / span;
      // The hem flares out over legs set apart in a stride or stance.
      const spread = Math.abs(rig.f2 - rig.f1) * 0.3;
      const hw = Math.round(6 + k * (p.shortRobe ? 1.5 : 3) + k * k * (rig.flare + spread));
      const hs = Math.round(lean * (1 - k) + k * k * -sway * 1.5);
      if (view === 'front') {
        // Front flaps meeting at the middle, the inner robe showing in a narrow V.
        const gap = Math.round(k * 2);
        box(g, cx - hw + hs, y, hw * 2 + 1, 1, p.outer);
        box(g, cx - hw + hs, y, 2, 1, p.outerS);
        box(g, cx + hw - 1 + hs, y, 2, 1, p.outerS);
        box(g, cx - gap + hs, y, gap * 2 + 1, 1, p.inner);
        box(g, cx - gap - 1 + hs, y, 1, 1, p.trim);
        box(g, cx + gap + 1 + hs, y, 1, 1, p.trim);
      } else if (back) {
        // Closed back panel with a centre seam.
        box(g, cx - hw + hs, y, hw * 2 + 1, 1, p.outer);
        box(g, cx - hw + hs, y, 2, 1, p.outerS);
        box(g, cx + (view === 'back' ? 0 : 1) + hs, y, 1, 1, p.outerS);
        if (!square && k > 0.3 && y % 3 !== 0) {
          box(g, cx - Math.round(hw * 0.5) + hs, y, 1, 1, p.outerS);
          box(g, cx + Math.round(hw * 0.6) + hs, y, 1, 1, p.outerS);
        }
      } else {
        box(g, cx - hw + hs, y, hw * 2 + 1, 1, p.inner);
        box(g, cx - hw + hs, y, hw + 2, 1, p.outer);
        box(g, cx + 3 + hs, y, hw - 2, 1, p.outer);
        box(g, cx - hw + hs, y, 2, 1, p.outerS);
        if (k > 0.25 && y % 2 === 0) box(g, cx - 3 + hs, y, 1, 1, p.outerS);
        // Folds: a crease in the back panel and one in the front flap, each with a lit edge.
        if (k > 0.35) {
          const bf = cx - Math.round(hw * 0.65) + hs;
          box(g, bf, y, 1, 1, p.outerS);
          if (y % 2) box(g, bf + 1, y, 1, 1, p.outerH);
          const ff = cx + 3 + Math.round((hw - 3) * 0.55) + hs;
          box(g, ff, y, 1, 1, p.outerS);
          if (y % 2 === 0) box(g, ff + 1, y, 1, 1, p.outerH);
        }
        box(g, cx + 3 + hs, y, 1, 1, p.trim);
      }
      if (y === skirtTop) box(g, cx - hw + hs, y, hw * 2 + 1, 1, p.outerS); // shadow under the sash
      if (square && k > 0.3 && y % 3 !== 0) {
        // Soft vertical folds in the hanging cloth.
        const fx = Math.round(hw * 0.55);
        box(g, cx - fx + hs, y, 1, 1, p.outerS);
        box(g, cx + fx + hs, y, 1, 1, p.outerS);
      }
      if (p.motif && y % 4 === 1)
        for (let x = -hw + 2; x <= hw - 1; x += 4) box(g, cx + x + hs + ((y >> 2) % 2) * 2, y, 1, 1, p.motif);
      if (y >= skirtBot - 1) box(g, cx - hw + hs, y, hw * 2 + 1, 1, p.trim);
      else if (y === skirtBot - 2 && !p.shortRobe) box(g, cx - hw + hs, y, hw * 2 + 1, 1, p.outerS);
      if (p.shortRobe && y === skirtBot && y % 2) box(g, cx - hw + hs + 3, y, 2, 1, p.inner);
    }
  }

  // ── weapon held behind the body ──
  upper();
  // The hanfu hand comes out of the middle of its wide cuff, held a little away from the body.
  const hand = { x: cx + (hanfu ? 9 : 7) + armX + rig.armSwing, y: (hanfu ? 39 : 37) + o };
  const staffLevel = rig.arm === 'forward' || rig.arm === 'cut' || rig.arm === 'thrust';
  if (p.weapon === 'staff' && !sit && !staffLevel) {
    // Walking staff held clear of the head, with a jade charm tied below the knob.
    const top = 12 + o;
    lower();
    box(g, cx + 12, top, 2, FY - top, p.blade);
    box(g, cx + 12, top, 1, FY - top, p.bladeS);
    ell(g, cx + 13, top - 1, 2.5, 2, p.blade);
    box(g, cx + 14, top + 3, 1, 2, p.tassel);
    box(g, cx + 14, top + 5, 2, 2, p.guard);
    upper();
  } else if (armed) {
    const L = p.wlen;
    if (rig.arm === 'hang') {
      const tx = hand.x + 2 + Math.round(L * 0.45);
      const ty = Math.min(FYu - 1, hand.y + 2 + Math.round(L * 0.8));
      line(g, hand.x + 1, hand.y + 2, tx, ty, p.blade);
      line(g, hand.x + 2, hand.y + 2, tx + 1, ty, p.bladeS);
      if (p.weapon === 'sabre') line(g, hand.x + 3, hand.y + 2, tx + 1, ty - 2, p.blade);
      line(g, hand.x - 2, hand.y - 3, hand.x, hand.y, p.hilt);
      box(g, hand.x - 1, hand.y + 1, 4, 1, p.guard);
      if (p.weapon === 'sword') {
        const s = Math.round(sway);
        box(g, hand.x - 3, hand.y - 3, 1, 4, p.tassel);
        box(g, hand.x - 4 - s, hand.y + 1, 1, 3, p.tassel);
      }
    } else if (rig.arm === 'raise') {
      line(g, cx + 6, 17 + o, cx - 9, 2 + o, p.blade);
      line(g, cx + 7, 17 + o, cx - 8, 2 + o, p.bladeS);
      box(g, cx + 5, 17 + o, 3, 1, p.guard);
    } else if (sit) {
      line(g, cx - 13, FYu - 6, cx + 14, FYu - 6, p.blade);
      line(g, cx - 13, FYu - 5, cx + 14, FYu - 5, p.bladeS);
      box(g, cx + 14, FYu - 7, 4, 3, p.hilt);
      box(g, cx + 13, FYu - 7, 1, 3, p.guard);
    } else if (lim && !lim.front) blade(cx + lim.hand[0] + armX, lim.hand[1] + o, lim.ang);
  }

  // ── torso ──
  for (let y = 24 + o; y < waist; y++) {
    const hw = y === 24 + o ? 5 : 6;
    box(g, cx - hw, y, hw * 2 + 1, 1, p.outer);
    box(g, cx - hw, y, 2, 1, p.outerS);
    if (p.motif && y % 4 === 3) box(g, cx - hw + 3 + ((y >> 2) % 2) * 3, y, 1, 1, p.motif);
  }
  box(g, cx - 3, 25 + o, 5, 1, p.outerH);
  if (p.stole && !back) {
    // Fur stole: a thick soft band from the near shoulder down across the body to the far hip.
    const y0 = 23 + o;
    const y1 = waist + 8;
    for (let y = y0; y <= y1; y++) {
      const f = (y - y0) / (y1 - y0);
      const sx = Math.round(cx + 6 - f * 13);
      box(g, sx - 2, y, 5, 1, p.stole.c);
      box(g, sx + 2, y, 1, 1, p.stole.s);
      if (y % 3 === 0) box(g, sx - 3, y, 1, 1, p.stole.c);
    }
    ell(g, cx + 7, y0 + 1, 4, 3, p.stole.c);
  }
  if (view === 'front' && !p.sleeveless) {
    // Cross collar seen head-on: right lapel over left, inner robe in the V.
    box(g, cx - 1, 23 + o, 3, 2, p.skin);
    for (let i = 0; i < 5; i++) box(g, cx - 3 + i, 24 + o + i, Math.max(1, 6 - i * 2), 1, p.inner);
    line(g, cx - 3, 24 + o, cx, 28 + o, p.trim);
    line(g, cx + 3, 24 + o, cx - 2, 31 + o, p.trim);
  } else if (view === 'front') {
    box(g, cx - 3, 24 + o, 7, 6, p.skin);
    box(g, cx - 1, 28 + o, 3, 2, p.skinS);
    line(g, cx - 4, 24 + o, cx - 2, 31 + o, p.trim);
    line(g, cx + 4, 24 + o, cx + 2, 31 + o, p.trim);
  } else if (back) {
    // Back of the robe: collar band at the nape and a centre seam.
    box(g, cx - 2, 23 + o, 6, 2, p.skinS);
    box(g, cx - 3, 24 + o, 8, 1, p.inner);
    box(g, cx - 3, 25 + o, 8, 1, p.sleeveless ? p.skinS : p.trim);
    for (let y = 26 + o; y < waist; y++) box(g, cx + (view === 'back' ? 0 : 1), y, 1, 1, p.outerS);
    if (p.sleeveless) box(g, cx - 4, 26 + o, 10, 7, p.outer);
  } else if (p.sleeveless) {
    box(g, cx - 1, 24 + o, 5, 6, p.skin);
    box(g, cx, 28 + o, 3, 2, p.skinS);
    line(g, cx - 2, 24 + o, cx + 1, 31 + o, p.trim);
    line(g, cx + 5, 24 + o, cx + 3, 31 + o, p.trim);
  } else {
    // Cross collar, right over left, with the inner robe showing.
    box(g, cx - 1, 23 + o, 4, 2, p.skin);
    for (let i = 0; i < 7; i++) {
      box(g, cx - 2 + i, 24 + o + i, 2, 1, p.inner);
      box(g, cx + i, 24 + o + i, 1, 1, p.trim);
    }
    box(g, cx - 3, 24 + o, 1, 3, p.trim);
    box(g, cx - 2, 24 + o, 1, 2, p.inner);
    // A soft fold from the shoulder down to the sash.
    line(g, cx - 4, 27 + o, cx - 3, 32 + o, p.outerS);
  }
  // Overcoat: open at the front to show the robe's collar, its skirts hanging below the sash.
  if (p.coat && !sit) {
    const C = p.coat;
    const coatLit = hexOf(rgbOf(C.c).map((v) => v + (255 - v) * 0.16));
    const span = Math.max(1, skirtBot - skirtTop);
    // Chest rows are drawn in design coordinates; the skirts below the sash directly on the canvas.
    const len = Math.round(C.len * 1.6);
    for (let y = 24 + o; y < skirtTop + len; y++) {
      if (y === waist) {
        mapped = false;
        y = skirtTop;
      }
      const below = y >= skirtTop;
      const k = below ? (y - skirtTop) / span : 0;
      const hw = below ? Math.round(7 + k * 3 + k * k * rig.flare) : y === 24 + o ? 5 : 6;
      const hs = below ? Math.round(lean * (1 - k) + k * k * -sway * 1.5) - lean : 0;
      const hem = y === skirtTop + len - 1;
      const L = cx - hw + hs;
      const R = cx + hw + hs;
      /** A crease in the coat skirt at `x`, with a lit edge on alternate rows. */
      const crease = (x: number) => {
        if (!below || k < 0.2 || hem) return;
        box(g, x, y, 1, 1, C.s);
        if (y % 2) box(g, x + 1, y, 1, 1, coatLit);
      };
      if (back) {
        // Seen from behind: one panel, split up the back below the sash.
        box(g, L, y, R - L + 1, 1, hem ? C.edge : C.c);
        box(g, L, y, 2, 1, C.s);
        box(g, R, y, 1, 1, C.edge);
        box(g, L, y, 1, 1, C.edge);
        if (below && y > skirtTop + 1) box(g, cx + (view === 'back' ? 0 : 1) + hs, y, 1, 1, C.edge);
        crease(L + Math.round((R - L) * 0.25));
        crease(L + Math.round((R - L) * 0.72));
      } else if (view === 'front') {
        const gap = below ? 2 + Math.round(k * 2) : 3;
        box(g, L, y, cx - gap - L, 1, hem ? C.edge : C.c);
        box(g, cx + gap + 1 + hs, y, R - (cx + gap + hs), 1, hem ? C.edge : C.c);
        box(g, L, y, 1, 1, C.s);
        box(g, cx - gap - 1 + hs, y, 1, 1, C.edge);
        box(g, cx + gap + 1 + hs, y, 1, 1, C.edge);
        crease(L + Math.round((cx - gap - L) * 0.45));
        crease(cx + gap + 1 + hs + Math.round((R - (cx + gap + 1 + hs)) * 0.5));
      } else {
        // Three-quarter or profile: the back panel, and the front panel's embroidered edge.
        // Below the sash the front panel meets the robe's own opening, so their edges coincide.
        const front = below ? cx + 3 + hs : cx + 4;
        box(g, L, y, (below ? cx - 1 + hs : cx - 2) - L, 1, hem ? C.edge : C.c);
        box(g, L, y, 2, 1, C.s);
        box(g, (below ? cx - 1 + hs : cx - 2) - 1, y, 1, 1, C.edge);
        box(g, front, y, R - front + 1, 1, hem ? C.edge : C.c);
        box(g, front, y, 1, 1, C.edge);
        crease(L + Math.round(((below ? cx - 1 + hs : cx - 2) - L) * 0.45));
        crease(front + Math.round((R - front) * 0.5));
      }
    }
    mapped = true;
  }
  // Sash, knot, hanging tails, jade pendant with tassel.
  box(g, cx - 6, 34 + o, 13, 3, p.sash);
  box(g, cx - 6, 36 + o, 13, 1, p.sashS);
  box(g, cx - 5, 34 + o, 11, 1, hexOf(rgbOf(p.sash).map((v) => v + (255 - v) * 0.22)));
  if (!back) box(g, cx + (view === 'front' ? -1 : 1), 34 + o, 3, 3, p.sashS);
  if (back) {
    // The scabbard hangs at the hip nearest the camera, its tip pointing back toward the viewer.
    scabbard(cx - 5, 36 + o, cx - 13, 49 + o);
    backHair();
    upper();
  } else if (!sit) {
    const s = Math.round(-sway);
    box(g, cx + 1 + s, 37 + o, 1, 6, p.sash);
    box(g, cx + 3 + s, 37 + o, 1, 5, p.sashS);
    box(g, cx + 1 + s, 43 + o, 1, 1, p.trim);
    box(g, cx + 3 + s, 42 + o, 1, 1, p.trim);
    if (p.pendant) {
      box(g, cx - 4, 37 + o, 1, 3, p.sashS);
      box(g, cx - 5, 40 + o, 3, 2, p.pendant);
      box(g, cx - 4 + s, 42 + o, 1, 3, p.tassel);
    }
  }

  // ── pelt slung over the shoulder, across the chest (or back) ──
  if (p.pelt) {
    const P = p.pelt;
    limb(g, cx - 6, 24 + o, cx + 5, 33 + o, 4, 3, P.c, P.s);
    ell(g, cx - 6, 24 + o, 3.5, 2.5, P.c);
    // Ragged fur tufts along both edges.
    for (let i = 0; i <= 10; i += 2) {
      const x = cx - 6 + i;
      const y = 24 + o + Math.round(i * 0.85);
      box(g, x, y - 3, 1, 1, P.c);
      box(g, x + 1, y + 2, 1, 1, P.s);
    }
  }

  // ── near sleeve and hand ──
  if (square && !sit) {
    // Rounded shoulders so both sleeves grow out of the torso.
    const sh = p.sleeveless ? p.skin : p.outer;
    box(g, cx + 4, 24 + o, 4, 2, sh);
    box(g, cx - 7, 24 + o, 4, 2, sh);
    box(g, cx - 7, 25 + o, 1, 1, p.sleeveless ? p.skinS : p.outerS);
  }
  const sleeveFill = p.sleeveless ? p.skin : p.outer;
  if (lim) {
    const hx = cx + lim.hand[0] + armX;
    const hy = lim.hand[1] + o;
    const sx = cx + 3 + armX;
    const sy = 26 + o;
    const ex = Math.round(sx + (hx - sx) * 0.8);
    const ey = Math.round(sy + (hy - sy) * 0.8);
    limb(g, sx, sy, ex, ey, 4, hanfu ? 7 : 5, sleeveFill, p.sleeveless ? p.skinS : p.outerS);
    if (hanfu && !p.sleeveless) {
      // The wide cuff hangs open below the forearm, lined in the inner robe's colour.
      box(g, ex - 2, ey, 5, 3, p.outerS);
      box(g, ex - 2, ey + 2, 5, 1, p.inner);
      box(g, ex - 2, ey + 3, 5, 1, p.trim);
    }
    box(g, ex - 1, ey - 1, 3, 3, p.sleeveless ? p.inner : p.trim);
    if (!armed && (rig.arm === 'thrust' || rig.arm === 'out')) {
      // Open palm thrust forward, fingers up.
      box(g, hx - 1, hy - 2, 3, 4, p.skin);
      box(g, hx + 2, hy - 3, 1, 4, p.skin);
      box(g, hx - 1, hy + 1, 3, 1, p.skinS);
    } else if (!armed && rig.arm === 'chest') {
      // Palms pressed together in front of the chest.
      box(g, hx - 3, hy - 3, 2, 5, p.skin);
      box(g, hx - 3, hy + 1, 2, 1, p.skinS);
    } else box(g, hx - 1, hy - 1, 2, 2, p.skin);
  } else if (rig.arm === 'forward') {
    box(g, cx + 3, 25 + o, 9, 4, sleeveFill);
    if (!p.sleeveless) {
      box(g, cx + 5, 29 + o, 6, 3, p.outerS);
      box(g, cx + 11, 25 + o, 1, 4, p.trim);
    }
    box(g, cx + 12, 25 + o, 2, 3, p.skin);
  } else if (rig.arm === 'guard') {
    box(g, cx + 3, 25 + o, 6, 4, sleeveFill);
    if (!p.sleeveless) box(g, cx + 4, 29 + o, 5, 4, p.outerS);
    box(g, cx + 9, 24 + o, 2, 4, p.skin);
    if (p.weapon === 'none') box(g, cx + 7, 21 + o, 2, 4, p.skin);
  } else if (rig.arm === 'raise') {
    box(g, cx + 3, 18 + o, 4, 8, sleeveFill);
    if (!p.sleeveless) box(g, cx + 1, 21 + o, 3, 8, p.outerS);
    box(g, cx + 4, 16 + o, 2, 2, p.skin);
  } else if (sit) {
    box(g, cx - 2, 33 + o, 9, 3, p.outerS);
    box(g, cx + 1, 33 + o, 3, 2, p.skin);
  } else if (p.sleeveless) {
    box(g, cx + 4 + armX + rig.armSwing, 25 + o, 3, 11, p.skin);
    box(g, cx + 4 + armX + rig.armSwing, 33 + o, 3, 2, p.inner);
    box(g, hand.x - 2, hand.y - 1, 3, 2, p.skin);
  } else if (hanfu) {
    // A bell sleeve: narrow at the shoulder, widening into an open cuff the hand comes out of.
    for (let y = 25 + o; y <= 39 + o; y++) {
      const k = (y - 25 - o) / 14;
      const w = Math.round(3 + k * k * 6);
      const x0 = cx + 3 + armX + Math.round(k * (rig.armSwing - sway) + k * 3.5) - Math.round(k * k * 2);
      box(g, x0, y, w, 1, y === 39 + o ? p.trim : y === 38 + o ? p.inner : p.outer);
      if (y < 38 + o) {
        // A dark seam where the sleeve hangs over the robe, so the two blues stay apart.
        box(g, x0, y, 1, 1, p.outerS);
        if (k > 0.3) box(g, x0 - 1, y, 1, 1, p.outerS);
        if (k > 0.4) box(g, x0 + Math.round(w * 0.55), y, 1, 1, p.outerS);
        if (k > 0.2 && y % 2) box(g, x0 + w - 2, y, 1, 1, p.outerH);
      }
    }
    box(g, hand.x - 1, hand.y, 2, 2, p.skin);
  } else {
    for (let y = 25 + o; y <= 37 + o; y++) {
      const k = (y - 25 - o) / 12;
      const w = Math.round(3 + k * 3);
      const x0 = cx + 3 + armX + Math.round(k * (rig.armSwing - sway));
      box(g, x0, y, w, 1, y === 37 + o ? p.trim : p.outer);
      box(g, x0, y, 1, 1, p.outerS);
    }
    box(g, hand.x - 1, hand.y, 2, 2, p.skin);
  }

  // ── shoulder plates over the sleeves ──
  if (p.pauldron) {
    const A = p.pauldron;
    const plate = (x: number, w: number) => {
      ell(g, x, 26 + o, w, 2.8, A.c);
      box(g, Math.round(x - w) + 1, 28 + o, Math.round(w * 2) - 1, 1, A.s); // lacquered edge
      box(g, Math.round(x), 22 + o, 1, 2, A.c); // spike
    };
    if (square) {
      plate(cx - 7, 4);
      plate(cx + 8, 4);
    } else if (view === 'side') plate(cx + 3, 4.5);
    else {
      plate(cx - 6, 3);
      plate(cx + 6, 4.5);
    }
  }

  // ── weapon in front ──
  if (armed && rig.arm === 'forward') {
    line(g, cx + 14, 23 + o, cx + 22, 4 + o, p.blade);
    line(g, cx + 15, 23 + o, cx + 23, 5 + o, p.bladeS);
    if (p.weapon === 'sabre') line(g, cx + 16, 23 + o, cx + 23, 8 + o, p.blade);
    box(g, cx + 12, 24 + o, 4, 1, p.guard);
    box(g, cx + 11, 27 + o, 1, 4, p.tassel);
  } else if (armed && rig.arm === 'guard') {
    line(g, cx + 11, 26 + o, cx + 25, 15 + o, p.blade);
    line(g, cx + 11, 27 + o, cx + 25, 16 + o, p.bladeS);
    box(g, cx + 9, 26 + o, 1, 3, p.guard);
  } else if (armed && lim?.front) blade(cx + lim.hand[0] + armX, lim.hand[1] + o, lim.ang);
  else if (p.weapon === 'staff' && staffLevel) {
    box(g, cx + 6, 26 + o, 20, 2, p.blade);
    box(g, cx + 6, 27 + o, 20, 1, p.bladeS);
  }

  // ── head ──
  upper(rig.headDx);
  const hy = 14 + o;
  /** The three-quarter face's eyes, brows, mouth and beard. */
  const features = () => {
    if (rig.closed) {
      box(g, cx + 1, hy + 3, 2, 1, p.eye);
      box(g, cx + 5, hy + 3, 3, 1, p.eye);
    } else if (p.iris) {
      animeFace(g, p, cx, hy, p.poseFaces?.[rig.face] ?? p.faceStyle ?? 'anime');
    } else {
      box(g, cx + 1, hy - 1, 2, 1, p.brow);
      box(g, cx + 5, hy - 1, 3, 1, p.brow);
      if (!p.evil) box(g, cx + 8, hy - 2, 1, 1, p.brow);
      box(g, cx + 1, hy + 1, 2, 3, p.eye);
      box(g, cx + 5, hy + 1, 3, 1, p.eye);
      box(g, cx + 5, hy + 2, 2, 2, p.eye);
      if (!p.evil) {
        box(g, cx + 6, hy + 2, 1, 1, '#ffffff');
        box(g, cx + 2, hy + 2, 1, 1, '#ffffff');
      }
    }
    if (!p.iris || rig.closed) {
      box(g, cx + 10, hy + 5, 1, 1, p.skinS);
      box(g, cx + 5, hy + 8, 2, 1, p.mouth);
      box(g, cx + 7, hy + 7, 1, 1, p.mouth);
    }
    if (p.beard === 'stubble')
      // Stubble dotted along the chin, clear of the mouth and cheeks.
      for (const [x, y] of [
        [1, 7],
        [2, 8],
        [4, 9],
        [6, 9],
        [7, 8],
      ] as const)
        box(g, cx + x, hy + y, 1, 1, '#8a6248');
    if (p.beard === 'long') {
      const b = p.beardColor ?? '#eeeeee';
      box(g, cx + 3, hy + 5, 7, 2, b);
      box(g, cx + 3, hy + 7, 6, 4, b);
      box(g, cx + 4, hy + 11, 4, 4, b);
      box(g, cx + 5, hy + 15, 2, 3, b);
      box(g, cx + 6, hy + 5, 2, 1, p.mouth);
    }
    if (p.mask) {
      box(g, cx + 2, hy + 4, 8, 3, p.mask);
      box(g, cx + 2, hy + 7, 7, 1, p.mask);
      box(g, cx + 2, hy + 8, 6, 1, p.mask);
      box(g, cx + 4, hy + 9, 3, 1, p.mask);
    }
  };
  ell(g, cx + 1, hy, 9.5, 9.5, p.hair);
  if (view === 'back') {
    // Straight from behind: all hair, both ears just peeking out.
    box(g, cx - 9, hy + 2, 2, 4, p.skinS);
    box(g, cx + 10, hy + 2, 2, 4, p.skinS);
    box(g, cx - 1, hy + 9, 5, 1, p.skinS);
    line(g, cx - 3, hy - 6, cx - 5, hy + 4, p.hairH);
    line(g, cx + 1, hy - 7, cx + 1, hy + 6, p.hairH);
    line(g, cx + 5, hy - 6, cx + 7, hy + 4, p.hairH);
    box(g, cx - 2, hy - 8, 6, 1, p.hairH);
    const hs = strand(p);
    line(g, cx - 1, hy - 7, cx - 2, hy + 6, hs);
    line(g, cx + 3, hy - 7, cx + 4, hy + 6, hs);
    line(g, cx - 6, hy - 4, cx - 7, hy + 3, hs);
  } else if (view === 'front') {
    frontFace(g, p, cx + 1, hy, rig.closed ? 'closed' : rig.face);
  } else if (view === 'side') {
    sideFace(g, p, cx, hy, rig.closed ? 'closed' : rig.face);
  } else if (back) {
    // Back of the head: all hair, with only the ear and the edge of the cheek showing.
    box(g, cx + 7, hy + 1, 3, 6, p.skin);
    box(g, cx + 8, hy + 7, 2, 2, p.skin);
    box(g, cx + 7, hy + 2, 1, 3, p.skinS);
    box(g, cx + 9, hy + 3, 1, 1, p.skinS);
    box(g, cx - 1, hy + 9, 4, 1, p.hair);
    // Strands and shine running down from the crown.
    line(g, cx - 4, hy - 6, cx - 6, hy + 3, p.hairH);
    line(g, cx + 1, hy - 7, cx, hy + 5, p.hairH);
    line(g, cx + 5, hy - 6, cx + 6, hy + 1, p.hairH);
    box(g, cx - 3, hy - 8, 6, 1, p.hairH);
    const hs = strand(p);
    line(g, cx - 2, hy - 7, cx - 3, hy + 4, hs);
    line(g, cx + 3, hy - 7, cx + 3, hy + 3, hs);
  } else {
    // Full cheekbones, then a jaw tapering to a pointed chin under the turned face.
    faceShape(g, p, cx + 3.8, hy + 2.5, 6.6, 7, cx, hy + 6, [
      [-1, 9],
      [0, 8],
      [1, 8],
      [3, 7],
      [4, 6],
    ]);
    box(g, cx - 3, hy + 1, 1, 5, p.skinS);
    box(g, cx - 2, hy + 2, 2, 3, p.skin);
    box(g, cx - 2, hy + 3, 1, 1, p.skinS);
    // Fringe and the long lock framing the near side of the face.
    box(g, cx - 3, hy - 6, 13, 3, p.hair);
    const curtainLike = p.hairStyle === 'curtain' || p.hairStyle === 'buns' || p.hairStyle === 'loose';
    if (curtainLike) {
      // Heavy bangs parted just off-centre, sweeping out and down to frame the face.
      box(g, cx - 4, hy - 6, 15, 2, p.hair);
      for (let r = 0; r < 4; r++) box(g, cx - 4, hy - 4 + r, Math.max(1, Math.round(6 - r * 1.5)), 1, p.hair);
      for (let r = 0; r < 5; r++) box(g, cx + 4 + r, hy - 4 + r, 7 - r, 1, p.hair);
      box(g, cx - 4, hy - 1, 2, 7, p.hair);
      box(g, cx + 10, hy - 2, 2, 9, p.hair);
      box(g, cx + 11, hy + 7, 1, 2, p.hair);
      line(g, cx - 2, hy - 5, cx, hy - 3, p.hairH);
      line(g, cx + 6, hy - 5, cx + 8, hy - 3, p.hairH);
    } else if (p.hairStyle === 'bun' || p.hairStyle === 'messy') {
      // Spiky fringe: pointed tufts plus one strand falling between the eyes.
      for (const x of [-1, 2, 6, 9]) {
        box(g, cx + x, hy - 3, 2, 1, p.hair);
        box(g, cx + x + (x < 4 ? 0 : 1), hy - 2, 1, 1, p.hair);
      }
      box(g, cx - 8, hy - 5, 1, 2, p.hair);
      box(g, cx - 9, hy - 2, 1, 2, p.hair);
      box(g, cx - 4, hy - 10, 1, 1, p.hair);
    } else if (p.hairStyle === 'ponytail') {
      box(g, cx, hy - 3, 2, 2, p.hair);
      box(g, cx + 4, hy - 3, 1, 3, p.hair);
      box(g, cx + 7, hy - 3, 2, 3, p.hair);
      box(g, cx + 9, hy - 2, 2, 9, p.hair);
      box(g, cx + 10, hy + 7, 1, 3, p.hair);
    }
    if (p.hairStyle === 'messy' || p.hairStyle === 'bun') {
      box(g, cx - 6, hy - 9, 2, 2, p.hair);
      box(g, cx + 6, hy - 9, 2, 1, p.hair);
    }
    box(g, cx - 4, hy - 8, 5, 1, p.hairH);
    box(g, cx + 2, hy - 8, 3, 1, p.hairH);
    box(g, cx - 6, hy - 6, 1, 2, p.hairH);
    // Strand lines: down the back of the head behind the ear, and between locks of the fringe.
    const hs = strand(p);
    line(g, cx - 7, hy - 4, cx - 8, hy + 3, hs);
    line(g, cx - 5, hy - 7, cx - 6, hy - 1, hs);
    line(g, cx - 1, hy - 9, cx - 2, hy - 7, hs);
    box(g, cx + 1, hy - 6, 1, 2, hs);
    box(g, cx + 6, hy - 6, 1, 2, hs);

    features();
  }

  // Headwear.
  if (p.headwear === 'guan') {
    // Small metal crown (guan) clasping the topknot, pierced by a hairpin.
    ell(g, cx, hy - 10, 3, 2.6, p.hair);
    box(g, cx - 1, hy - 13, 3, 4, p.crown);
    box(g, cx - 2, hy - 11, 5, 2, p.crown);
    box(g, cx - 2, hy - 10, 5, 1, p.crownS);
    box(g, cx, hy - 13, 1, 1, p.crownS);
    line(g, cx - 4, hy - 11, cx + 4, hy - 12, p.crownS);
  } else if (p.headwear === 'tiara') {
    // High topknot crowned with a small gold tiara and a red stone.
    ell(g, cx, hy - 10, 3.5, 3, p.hair);
    box(g, cx - 2, hy - 11, 6, 2, '#e8b84a');
    box(g, cx - 2, hy - 10, 6, 1, '#b07f24');
    box(g, cx - 2, hy - 12, 1, 1, '#e8b84a');
    box(g, cx + 1, hy - 13, 1, 2, '#e8b84a');
    box(g, cx + 3, hy - 12, 1, 1, '#e8b84a');
    box(g, cx + 1, hy - 11, 1, 1, '#d23a3a');
  } else if (p.headwear === 'gem') {
    // High bun wrapped in a cloth band with a jade stone.
    ell(g, cx - 1, hy - 10, 4.2, 3.6, p.hair);
    box(g, cx - 3, hy - 13, 3, 1, p.hairH);
    box(g, cx - 5, hy - 8, 8, 2, p.accent);
    box(g, cx - 5, hy - 7, 8, 1, p.sashS);
    box(g, cx - 1, hy - 9, 2, 3, p.crown);
    box(g, cx - 1, hy - 9, 1, 1, '#d4ffe6');
    box(g, cx, hy - 7, 1, 1, p.crownS);
  } else if (p.headwear === 'band') {
    box(g, cx - 7, hy - 5, 17, 2, p.accent);
    box(g, cx + 7, hy - 5, 2, 1, p.outerH);
  } else if (p.headwear === 'pin') {
    ell(g, cx, hy - 10, 3.5, 3, p.hair);
    box(g, cx - 2, hy - 12, 2, 1, p.hairH);
    line(g, cx - 5, hy - 9, cx + 5, hy - 11, p.crown);
  }
  if (p.hairStyle === 'buns') {
    ell(g, cx - 5, hy - 8, 3, 3, p.hair);
    ell(g, cx + 5, hy - 9, 3, 3, p.hair);
    box(g, cx - 6, hy - 10, 2, 1, p.hairH);
    box(g, cx + 4, hy - 11, 2, 1, p.hairH);
    box(g, cx + 7, hy - 8, 2, 2, p.accent);
    box(g, cx + 8, hy - 9, 1, 1, '#ffffff');
  }
  // Bamboo hat over everything on the head, its veil hanging clear of the face.
  if (p.hat) {
    const H = p.hat;
    const by = hy - 5;
    for (let r = 0; r < 8; r++) {
      const hw = Math.round(11 - r * 1.4);
      box(g, cx + 1 - hw, by - 1 - r, hw * 2 + 1, 1, r % 3 === 1 ? H.s : H.c);
    }
    ell(g, cx + 1, by, 14, 2.6, H.c);
    box(g, cx - 12, by + 1, 27, 1, H.s);
    box(g, cx, by - 9, 2, 1, H.s);
    if (H.veil) {
      // Sheer gauze: alternate columns let the background through.
      const cols = back ? [-12, -10, -8, -6, -4, -2, 0, 2, 4, 6, 8, 10, 12, 14] : [-12, -10, 12, 14];
      for (const dx of cols) {
        const len = 10 + ((dx + 13) % 3);
        box(g, cx + dx, by + 2, 1, len, H.veil);
      }
    }
  }
  if (p.foxEars) {
    // Fox spirit: two pointed ears with pale insides.
    for (const ex of view === 'side' || (!back && view !== 'front') ? [-3, 4] : [-6, 6]) {
      const ox = cx + 1 + ex;
      for (let r = 0; r < 7; r++) {
        const hw = Math.max(0, 2 - Math.floor(r / 3));
        box(g, ox - hw, hy - 8 - r, hw * 2 + 1, 1, p.foxEars.c);
        if (r < 4 && !back && hw > 0) box(g, ox, hy - 8 - r, 1, 1, p.foxEars.s);
      }
    }
  }
  g.setTransform(1, 0, 0, 1, 0, 0);
  if (partLines) drawPartLines(c, p);
  return p.selOut ? selOutline(litShade(c)) : figOutline(litShade(c));
}

/** หมีดำภูเขา: a hulking black bear on all fours, pale muzzle and a crescent on its chest. */
function bear(ph: number): HTMLCanvasElement {
  const c = canvas(36, 26);
  const g = ctx2d(c);
  const fur = '#2a2422';
  const sh = '#181414';
  const lt = '#4a403a';
  // The legs' swing round the stride (0, 1, 0, −1 on the four old steps), at any point of it.
  const lo = Math.round(Math.sin((ph * Math.PI) / 2));
  box(g, 8 - lo, 17, 4, 7, sh);
  box(g, 22 + lo, 17, 4, 7, sh);
  ell(g, 16, 13, 12, 7, fur);
  ell(g, 14, 9, 9, 3.5, lt);
  ell(g, 27, 11, 6, 5.5, fur);
  box(g, 30, 11, 5, 4, '#8a7462');
  box(g, 33, 11, 2, 2, '#141010');
  box(g, 24, 5, 3, 3, fur);
  box(g, 29, 5, 3, 3, fur);
  box(g, 30, 9, 1, 1, '#e8b84a');
  ell(g, 22, 15, 3, 1.5, '#d8d0c0');
  box(g, 12 + lo, 17, 4, 8, fur);
  box(g, 25 - lo, 17, 4, 8, fur);
  for (const x of [13, 26]) box(g, x + (x < 20 ? lo : -lo), 24, 3, 1, '#d8d0c0');
  return figOutline(c);
}

/**
 * The pet fox from any of the five camera angles (mirrored for the other three): side-on, a
 * three-quarter view toward or away from the viewer, head-on and from behind. Head, body and
 * bushy white-tipped tail are placed by how far the fox is turned across the screen, and drawn
 * nearest-last; its face shows when it comes toward the viewer.
 */
function fox(ph: number, view: View = 'front3'): HTMLCanvasElement {
  const c = canvas(30, 24);
  const g = ctx2d(c);
  const o = '#e8692a';
  const od = '#b9481c';
  const cr = '#f8e7c9';
  const dk = '#3b2420';
  // The legs' swing round the stride (0, 1, 0, −1 on the four old steps), at any point of it.
  const lo = Math.round(Math.sin((ph * Math.PI) / 2));
  const sw = -lo;
  // How far the snout points right (1) and whether it comes toward the viewer.
  const across = view === 'side' ? 1 : view === 'front3' || view === 'back3' ? 0.6 : 0;
  const toward = view === 'front' || view === 'front3';
  const back = !toward && view !== 'side';
  const cx = 15;
  const hx = Math.round(cx + across * 6);
  const hy = view === 'side' ? 11 : toward ? 12 : 8;
  const tx = Math.round(cx - across * 8);
  const ty = view === 'side' ? 10 : toward ? 8 : 15;
  const tail = () => {
    ell(g, tx, ty + sw, across ? 5.5 : 4.5, across ? 4.5 : 5.5, o);
    ell(g, tx - across * 3, ty - (across ? 3 : toward ? 3 : -3) + sw, 3, 2.6, cr);
  };
  const legs = (shift: number) => {
    box(g, cx - 5 + shift - lo, 17, 2, 4, od);
    box(g, cx + 4 + shift + lo, 17, 2, 4, od);
  };
  const body = () => {
    ell(g, cx, 15, across ? 6.5 : 5, 3.8, o);
    if (!back) ell(g, cx + across, 17, across ? 4.5 : 3.5, 1.6, cr);
  };
  const head = () => {
    ell(g, hx, hy, 4.8, 4.2, o);
    // Ears: side by side when seen side-on, either side of the head otherwise.
    const ears = across === 1 ? [hx - 4, hx + 1] : [hx - 4, hx + 2];
    for (const ex of ears) {
      box(g, ex, hy - 7, 3, 4, o);
      box(g, ex + 1, hy - 8, 1, 1, dk);
      if (!back) box(g, ex + 1, hy - 5, 1, 2, od);
    }
    if (back) return;
    if (across === 1) {
      ell(g, hx + 1.5, hy + 2.5, 3.2, 1.7, cr);
      box(g, hx + 2, hy - 1, 1, 2, dk);
      box(g, hx + 5, hy + 1, 1, 1, dk);
    } else if (across > 0) {
      ell(g, hx + 1, hy + 2.5, 3, 1.8, cr);
      box(g, hx - 2, hy - 1, 1, 2, dk);
      box(g, hx + 2, hy - 1, 1, 2, dk);
      box(g, hx + 3, hy + 2, 1, 1, dk);
    } else {
      ell(g, hx, hy + 2.5, 2.8, 1.8, cr);
      box(g, hx - 3, hy - 1, 1, 2, dk);
      box(g, hx + 2, hy - 1, 1, 2, dk);
      box(g, hx, hy + 2, 1, 1, dk);
    }
  };
  if (back) {
    legs(0);
    head();
    body();
    tail();
  } else {
    tail();
    legs(0);
    body();
    head();
  }
  box(g, cx - 3 + lo, 18, 2, 4, dk);
  box(g, cx + 5 - lo, 18, 2, 4, dk);
  return figOutline(c);
}

/** The fox's sprite set, with its four other camera angles. */
function foxSet(): SpriteSet {
  const base = beastSet((ph) => fox(ph), 15, 22, 22);
  base.views = Object.fromEntries(
    VIEWS.filter((v) => v !== 'front3').map((v) => [v, beastSet((ph) => fox(ph, v), 15, 22, 22)]),
  );
  return base;
}

function artistSet(p: Look, view: View = 'front3'): SpriteSet {
  const T = p.tall ?? 0;
  // Remember which pose each frame is, so it can be redrawn with fabric in motion.
  const meta = new Map<HTMLCanvasElement, [Pose, number]>();
  const draw = (pose: Pose, ph = 0) => {
    const c = martialArtist(p, pose, ph, view);
    meta.set(c, [pose, ph]);
    return c;
  };
  const moving = new Map<string, HTMLCanvasElement>();
  const cloth = (img: HTMLCanvasElement, sway: number, flutter: number) => {
    const m = meta.get(img);
    if (!m || (!sway && !flutter)) return img;
    const key = `${m[0]}:${m[1]}:${sway}:${flutter}`;
    let c = moving.get(key);
    if (!c) moving.set(key, (c = martialArtist(p, m[0], m[1], view, { sway, flutter })));
    return c;
  };
  return {
    cloth,
    idle: Array.from({ length: FRAMES }, (_, i) => draw('idle', i)),
    walk: Array.from({ length: WALK_FRAMES }, (_, i) => draw('walk', i)),
    sprint: Array.from({ length: WALK_FRAMES }, (_, i) => draw('sprint', i)),
    ready: Array.from({ length: FRAMES }, (_, i) => draw('ready', i)),
    atk: draw('atk'),
    block: draw('block'),
    cast: draw('cast'),
    sit: draw('sit'),
    jump: draw('jump'),
    armed: (p.weapon === 'sword' || p.weapon === 'sabre') && !p.sheathed,
    poses: Object.fromEntries(EXTRA_POSES.map((k) => [k, draw(k)])),
    views:
      view === 'front3'
        ? Object.fromEntries(VIEWS.filter((v) => v !== 'front3').map((v) => [v, artistSet(p, v)]))
        : undefined,
    ax: ART_CX,
    ay: 58 + T + PAD_TOP + BODY_EXTRA,
    h: 56 + T + BODY_EXTRA,
    drawScale: 1,
  };
}

function beastSet(draw: (ph: number) => HTMLCanvasElement, ax: number, ay: number, h: number): SpriteSet {
  // `FRAMES` frames round the stride; standing, the weight shifts slowly from one side to the other.
  const walk = Array.from({ length: FRAMES }, (_, i) => draw((i * 4) / FRAMES));
  return {
    idle: Array.from({ length: FRAMES }, (_, i) => walk[i < FRAMES / 2 ? 0 : FRAMES / 2]!),
    walk,
    atk: walk[Math.round(FRAMES / 4)]!,
    block: walk[0]!,
    cast: walk[Math.round(FRAMES / 4)]!,
    sit: walk[0]!,
    jump: walk[Math.round(FRAMES / 4)]!,
    poses: {},
    ax,
    ay,
    h,
  };
}

export const FACE_OPTIONS: readonly { id: FaceStyle; label: string }[] = [
  { id: 'refSerious', label: 'R1 · นิ่งเข้ม' },
  { id: 'refSmirk', label: 'R2 · ยิ้มมุมปาก' },
  { id: 'refGrit', label: 'R3 · กัดฟัน' },
  { id: 'refShout', label: 'R4 · ตะโกนออกท่า' },
  { id: 'refCold', label: 'R5 · ตาเรียวเย็นชา' },
];

/** The hero drawn with each face design, for choosing a look. */
export function buildFaceOptions(): { id: FaceStyle; label: string; set: SpriteSet }[] {
  return FACE_OPTIONS.map((f) => ({ ...f, set: artistSet({ ...hero, faceStyle: f.id, poseFaces: {} }) }));
}

/**
 * The running frames with fabric streaming, for every camera angle of a figure, as small jobs.
 * Running them ahead of time (when the browser is idle) avoids building frames mid-run.
 */
export function clothWarmJobs(set: SpriteSet): (() => void)[] {
  const jobs: (() => void)[] = [];
  for (const v of [set, ...Object.values(set.views ?? {})]) {
    // A stance's views (rig/sets.ts `inStance`) list every view, the ones the base set lacks as undefined.
    const cloth = v?.cloth;
    if (!v || !cloth) continue;
    // Each walk frame is read when its job runs, so a frame drawn since is used, not its stand-in.
    for (let i = 0; i < v.walk.length; i++)
      for (let sway = 1; sway <= 3; sway++)
        for (let flutter = 0; flutter < 4; flutter++) jobs.push(() => cloth(v.walk[i]!, sway, flutter));
  }
  return jobs;
}

/**
 * Sprite sets by entity: the base set for its sprite, or (for anyone wearing equipment) a set built
 * from the base look with each worn item's appearance laid over it, built on first use and cached.
 * `dress` draws a person from a look (the modular rig; see render/rig/sets.ts).
 */
export class SpriteBank {
  private readonly dressed = new Map<string, SpriteSet>();
  /** When each dressed figure was last asked for, to let go of the ones no longer around. */
  private readonly seen = new Map<string, number>();
  private sweptAt = 0;

  constructor(
    readonly base: Record<string, SpriteSet>,
    private readonly dress: (look: Look) => SpriteSet,
  ) {}

  of(e: { sprite: string; outfit?: readonly string[]; id?: number }): SpriteSet | undefined {
    const set = this.look(e);
    // Each person stands in a stance of their own, by who they are (see `stanceOf`).
    return e.id === undefined ? set : (set?.stance?.(stanceOf(e.id)) ?? set);
  }

  private look(e: { sprite: string; outfit?: readonly string[] }): SpriteSet | undefined {
    const look = LOOKS[e.sprite];
    if (!e.outfit?.length || !look) return this.base[e.sprite];
    const key = `${e.sprite}|${e.outfit.join(',')}`;
    const now = performance.now();
    this.seen.set(key, now);
    if (now - this.sweptAt > SWEEP_MS) this.sweep(now);
    let set = this.dressed.get(key);
    if (!set) {
      const dressed: Look = { ...look };
      for (const o of e.outfit) Object.assign(dressed, OUTFITS[o], swordLook(o));
      set = this.dress(dressed);
      this.dressed.set(key, set);
    }
    return set;
  }

  /**
   * Lets go of figures not shown for a while (players met and left behind), with every frame drawn
   * for them: in a busy world the bank would otherwise only grow. They are drawn again if met again.
   */
  private sweep(now: number): void {
    this.sweptAt = now;
    for (const [key, at] of this.seen)
      if (now - at > FORGET_MS) {
        this.seen.delete(key);
        this.dressed.delete(key);
      }
  }
}

/** How often the sprite bank looks for figures to forget, and how long unseen before it does. */
const SWEEP_MS = 10_000;
const FORGET_MS = 60_000;

/** The four-legged creatures (monsters and the pet fox), without building every humanoid. */
export function buildBeasts(): Record<string, SpriteSet> {
  // Every creature is set up the first time it is asked for (reading the sources' keys builds nothing).
  const sources: Record<string, SpriteSet>[] = [
    buildBestiary(snowify),
    lazyRecord({ bear: () => beastSet(bear, 17, 25, 24), fox: foxSet }),
    buildCritters(),
  ];
  const makers: Record<string, () => SpriteSet> = {};
  for (const src of sources)
    for (const k of Object.keys(src)) makers[k] = () => doubled(src[k]!, BEAST_DETAIL[k]);
  // The plains wolf as the 2026-10-04 client drew it (classicBeasts.ts), finished as it was.
  const classic = classicBeasts();
  for (const k of Object.keys(classic)) makers[k] = () => classic[k]!;
  return lazyRecord(makers);
}

/** What each creature's surface is made of, for the detail painted in at double resolution. */
const BEAST_DETAIL: Readonly<Record<string, Detail>> = {
  wolf: 'fur',
  snowwolf: 'fur',
  boar: 'fur',
  leopard: 'fur',
  whiteTiger: 'fur',
  foxSpirit: 'fur',
  bear: 'fur',
  fox: 'fur',
  cat: 'fur',
  bloodBat: 'fur',
  viper: 'scales',
  python: 'scales',
  centipede: 'chitin',
  scorpion: 'chitin',
  spider: 'chitin',
  chicken: 'feathers',
  bird: 'feathers',
};

const DOUBLED_SETS = new WeakMap<SpriteSet, SpriteSet>();
/**
 * A creature's sprite set finished for the world, like the scenery: every frame through
 * `finishArt` (its detail pass and a rim of light) the first time it is shown, the same finished
 * frame every time after. Anchors and heights stay in art pixels.
 */
export function doubled(set: SpriteSet, detail?: Detail): SpriteSet {
  const done = DOUBLED_SETS.get(set);
  if (done) return done;
  // Beasts are characters too: on the figure grid under option C (density.ts), their own 1× art in the 1x mode.
  const one = (c: HTMLCanvasElement) =>
    densityC() ? toFigureGrid(finishArt(c, true, detail)) : finishArt(c, true, detail);
  const many = (a: HTMLCanvasElement[]) =>
    new Proxy(a, {
      get(t, key, recv) {
        const v = Reflect.get(t, key, recv) as unknown;
        if (typeof key === 'string' && /^\d+$/.test(key)) return v ? one(v as HTMLCanvasElement) : v;
        if (key === Symbol.iterator)
          return function* () {
            for (const c of t) yield one(c);
          };
        return v;
      },
    });
  const lists = new Map<PropertyKey, HTMLCanvasElement[]>();
  const out = new Proxy(set, {
    get(t, key, recv) {
      const v = Reflect.get(t, key, recv) as unknown;
      if (v instanceof HTMLCanvasElement) return one(v);
      if (Array.isArray(v)) {
        let l = lists.get(key);
        if (!l) lists.set(key, (l = many(v as HTMLCanvasElement[])));
        return l;
      }
      if (key === 'poses' && v)
        return new Proxy(v as object, {
          get: (p, k, r) => {
            const c = Reflect.get(p, k, r) as unknown;
            return c instanceof HTMLCanvasElement ? one(c) : c;
          },
        });
      if (key === 'views' && v)
        return new Proxy(v as object, {
          get: (p, k, r) => {
            const s = Reflect.get(p, k, r) as SpriteSet | undefined;
            return s ? doubled(s, detail) : s;
          },
        });
      return v;
    },
  });
  DOUBLED_SETS.set(set, out);
  return out;
}

export function buildSprites(): Record<string, SpriteSet> {
  const out: Record<string, SpriteSet> = {};
  for (const [k, look] of Object.entries(LOOKS)) out[k] = artistSet(look);
  return Object.assign(out, buildBeasts());
}

/** Where a figure drawn by `martialArtist` stands (its feet) inside the frame. */
export function lookAnchor(p: Look): { ax: number; ay: number } {
  return { ax: ART_CX, ay: 58 + (p.tall ?? 0) + PAD_TOP + BODY_EXTRA };
}

/** Head-and-shoulders crop for portraits. */
export function drawPortrait(target: HTMLCanvasElement, set: SpriteSet): void {
  const g = ctx2d(target);
  g.imageSmoothingEnabled = false;
  g.clearRect(0, 0, target.width, target.height);
  const img = set.idle[0]!;
  // Art pixels (ax − 12, 1 + PAD_TOP) to 26 further, scaled to fill the target, wherever the frame was cut
  // (the B3 figure's smaller, higher head: a smaller, higher window, rig/b3/flag.ts).
  const pw = portraitWindow(artY(img) - PAD_TOP);
  const kx = target.width / pw.size;
  const ky = target.height / pw.size;
  g.drawImage(
    img,
    (artX(img) - (set.ax - pw.left)) * kx,
    1 + (artY(img) - (pw.top + PAD_TOP)) * ky,
    artW(img) * kx,
    artH(img) * ky,
  );
}
